import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  MapPin, 
  Volume2, 
  Sun, 
  Users, 
  Wind, 
  Thermometer, 
  Eye, 
  Box,
  Info,
  Loader2,
  RefreshCw,
  Download,
  Share2,
  Lock,
  ChevronRight,
  ChevronLeft,
  Building2,
  Ticket,
  Layers,
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  ImageIcon,
  HelpCircle,
  AlertCircle,
  Check,
  ArrowRight,
  Clock,
  Bell,
  Search,
  Home,
  Navigation,
  Coffee,
  DoorOpen,
  Shield,
  Heart,
  Play,
  Plus,
  Edit3,
  Trash2,
  Save
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ExperienceHubCore from '../components/ExperienceHubCore';
import { buildRouteForDestination } from '../lib/routeBuilder';

// Screen types for navigation - TIER 1 MVP with new Explore Hub & Preview Route
const SCREENS = {
  // NEW TIER 1 SCREENS
  EXPLORE_HUB: 'explore_hub',
  PREVIEW_ROUTE: 'preview_route',
  // LEGACY SCREENS (preserved as fallback)
  ENTRY: 'entry',
  ABOUT: 'about',
  SENSORY: 'sensory',
  AREAS: 'areas',
  BREAKDOWN: 'breakdown'
};

// Flag to toggle between new Tier 1 UI and legacy glassmorphism UI
const USE_TIER1_UI = true;

// ==================== IMAGE FALLBACK COMPONENT ====================
const AreaImage = ({ src, alt, className = '' }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  if (!src || imageError) {
    return (
      <div className={`bg-gradient-to-br from-slate-600/50 to-slate-800/50 backdrop-blur-sm flex flex-col items-center justify-center ${className}`}>
        <ImageIcon className="w-10 h-10 text-white/30 mb-2" />
        <span className="text-white/40 text-xs">No image available</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {imageLoading && (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-600/50 to-slate-800/50 animate-pulse flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-white/40 animate-spin" />
        </div>
      )}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'}`}
        onLoad={() => setImageLoading(false)}
        onError={() => {
          setImageError(true);
          setImageLoading(false);
        }}
        loading="lazy"
      />
    </div>
  );
};

// ==================== IMAGE GALLERY COMPONENT (Multi-image support) ====================
const ImageGallery = ({ photos, areaName, className = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [galleryOpen, setGalleryOpen] = useState(false);
  
  // Extract URLs from photos array [{url, order}]
  const imageUrls = (photos || [])
    .filter(p => p?.url)
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(p => p.url);
  
  if (imageUrls.length === 0) {
    return (
      <div className={`bg-gradient-to-br from-slate-600/50 to-slate-800/50 backdrop-blur-sm flex flex-col items-center justify-center ${className}`}>
        <ImageIcon className="w-10 h-10 text-white/30 mb-2" />
        <span className="text-white/40 text-xs">No image available</span>
      </div>
    );
  }
  
  if (imageUrls.length === 1) {
    return <AreaImage src={imageUrls[0]} alt={areaName} className={className} />;
  }

  return (
    <>
      {/* Main Image with Gallery Indicator */}
      <div className={`relative ${className} cursor-pointer`} onClick={() => setGalleryOpen(true)}>
        <AreaImage src={imageUrls[currentIndex]} alt={areaName} className="w-full h-full" />
        {/* Image count badge */}
        <div className="absolute bottom-2 right-2 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1">
          <ImageIcon className="w-3 h-3 text-white/80" />
          <span className="text-white text-xs font-medium">{currentIndex + 1}/{imageUrls.length}</span>
        </div>
        {/* Horizontal dots */}
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {imageUrls.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
              className={`w-2 h-2 rounded-full transition-all ${i === currentIndex ? 'bg-white w-4' : 'bg-white/50'}`}
            />
          ))}
        </div>
      </div>

      {/* Fullscreen Gallery Modal */}
      <AnimatePresence>
        {galleryOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={() => setGalleryOpen(false)}
          >
            {/* Close button */}
            <button 
              className="absolute top-4 right-4 p-2 text-white/80 hover:text-white z-10"
              onClick={() => setGalleryOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Image counter */}
            <div className="absolute top-4 left-4 text-white/80 text-sm">
              {currentIndex + 1} / {imageUrls.length}
            </div>
            
            {/* Main image */}
            <img 
              src={imageUrls[currentIndex]} 
              alt={`${areaName} - Image ${currentIndex + 1}`}
              className="max-w-full max-h-[80vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Navigation arrows */}
            {imageUrls.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => Math.max(0, i - 1)); }}
                  disabled={currentIndex === 0}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30"
                >
                  <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i => Math.min(imageUrls.length - 1, i + 1)); }}
                  disabled={currentIndex === imageUrls.length - 1}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white/10 hover:bg-white/20 rounded-full disabled:opacity-30"
                >
                  <ChevronRight className="w-6 h-6 text-white" />
                </button>
              </>
            )}
            
            {/* Thumbnail strip */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] p-2">
              {imageUrls.map((url, i) => (
                <button
                  key={i}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(i); }}
                  className={`w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all ${
                    i === currentIndex ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== SKELETON LOADER COMPONENTS ====================
const SkeletonCard = () => (
  <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 animate-pulse">
    <div className="h-4 bg-white/20 rounded w-3/4 mb-4"></div>
    <div className="h-3 bg-white/10 rounded w-1/2"></div>
  </div>
);

const SkeletonImage = () => (
  <div className="aspect-video rounded-3xl bg-gradient-to-br from-slate-600/30 to-slate-800/30 animate-pulse flex items-center justify-center">
    <ImageIcon className="w-8 h-8 text-white/20" />
  </div>
);

// ==================== SENSORY HELP TOOLTIP ====================
const SensoryHelpTooltip = ({ isOpen, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        className="absolute top-full left-0 right-0 mt-2 bg-white/95 backdrop-blur-xl rounded-2xl p-4 shadow-xl z-50"
      >
        <div className="flex items-start justify-between mb-3">
          <h4 className="font-semibold text-slate-800 text-sm">Understanding Sensory Scores</h4>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2 text-xs text-slate-600">
          <p className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-500"></span>
            <strong>Low (1-2):</strong> Calm, minimal sensory input
          </p>
          <p className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-amber-500"></span>
            <strong>Moderate (3):</strong> Noticeable but manageable
          </p>
          <p className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <strong>High (4-5):</strong> Intense, may be challenging
          </p>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ==================== MAIN COMPONENT ====================
const GuidePage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [guide, setGuide] = useState(null);
  const [venue, setVenue] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  
  // Screen navigation state - Start with EXPLORE_HUB for Tier 1 UI
  const [currentScreen, setCurrentScreen] = useState(USE_TIER1_UI ? SCREENS.EXPLORE_HUB : SCREENS.ENTRY);
  const [selectedArea, setSelectedArea] = useState(null);
  const [screenHistory, setScreenHistory] = useState([USE_TIER1_UI ? SCREENS.EXPLORE_HUB : SCREENS.ENTRY]);
  const [expandedAbout, setExpandedAbout] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showSensoryHelp, setShowSensoryHelp] = useState(false);
  
  // TIER 1: Explore Hub state
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [routeTargetArea, setRouteTargetArea] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showVenueAbout, setShowVenueAbout] = useState(false);
  const [brandColor, setBrandColor] = useState('#1e3a5f'); // Dynamic brand color

  
  // Journey Builder state
  const [journeyEditMode, setJourneyEditMode] = useState(false);
  const [movementSteps, setMovementSteps] = useState([]);
  const [showStepEditor, setShowStepEditor] = useState(false);
  const [editingStep, setEditingStep] = useState(null); // null for new, or step_id for edit
  const [stepEditorData, setStepEditorData] = useState({
    from_area_id: '',
    to_area_id: '',
    title: '',
    description: '',
    awareness: '',
    duration: '10-15 seconds'
  });
  const [savingStep, setSavingStep] = useState(false);
  
  // Weekly Busyness state (Owner-controlled visitor levels)
  const [submission, setSubmission] = useState(null);
  const [weeklyBusyness, setWeeklyBusyness] = useState(null);
  const [busynessNotes, setBusynessNotes] = useState('');
  const [showBusynessEditor, setShowBusynessEditor] = useState(false);
  const [savingBusyness, setSavingBusyness] = useState(false);
  
  // Subscription state
  const isSubscribed = user?.is_subscribed || false;
  
  // Check if current user is the owner
  const isOwner = submission?.user_id === user?.user_id;

  useEffect(() => {
    fetchAllData();
  }, [submissionId]);

  const fetchAllData = async () => {
    setError(null);
    try {
      const guideResponse = await api.get(`/submissions/${submissionId}/guide`);
      setGuide(guideResponse.data);
      
      // Fetch submission data for weekly busyness
      try {
        const submissionResponse = await api.get(`/submissions/${submissionId}`);
        setSubmission(submissionResponse.data);
        setWeeklyBusyness(submissionResponse.data.weekly_busyness || null);
        setBusynessNotes(submissionResponse.data.busyness_notes || '');
      } catch (subErr) {
        // Submission might not be accessible, that's OK
      }
      
      if (guideResponse.data.venue_id) {
        const venueResponse = await api.get(`/venues/${guideResponse.data.venue_id}`);
        setVenue(venueResponse.data);
        
        // Load brand color with fallback chain
        const color = guideResponse.data?.branding?.primary_color || 
                      venueResponse.data?.brand_color_1 || 
                      '#1e3a5f';
        setBrandColor(color);
        
        const areasResponse = await api.get(`/areas/venue/${guideResponse.data.venue_id}`);
        setAreas(areasResponse.data || []);
      }
      
      // Fetch movement steps for this submission
      try {
        const stepsResponse = await api.get(`/movement-steps/submission/${submissionId}`);
        setMovementSteps(stepsResponse.data || []);
      } catch (stepErr) {
        // Movement steps might not exist yet, that's OK
        setMovementSteps([]);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error('Guide not found. Please generate one first.');
        navigate(`/submission/${submissionId}/summary`);
      } else {
        setError('Something went wrong loading this experience');
      }
    } finally {
      setLoading(false);
    }
  };

  // ==================== JOURNEY BUILDER FUNCTIONS ====================
  
  // Get movement step for a specific pair (FROM → TO)
  const getMovementStepForPair = useCallback((fromAreaId, toAreaId) => {
    return movementSteps.find(
      s => s.from_area_id === fromAreaId && s.to_area_id === toAreaId
    );
  }, [movementSteps]);

  // Open step editor for adding new step
  const openAddStepEditor = (fromAreaId, toAreaId, fromName, toName) => {
    setEditingStep(null);
    setStepEditorData({
      from_area_id: fromAreaId,
      to_area_id: toAreaId,
      from_name: fromName,
      to_name: toName,
      title: '',
      description: '',
      awareness: '',
      duration: '10-15 seconds'
    });
    setShowStepEditor(true);
  };

  // Open step editor for editing existing step
  const openEditStepEditor = (step) => {
    setEditingStep(step.step_id);
    setStepEditorData({
      from_area_id: step.from_area_id,
      to_area_id: step.to_area_id,
      title: step.title || '',
      description: step.description || '',
      awareness: step.awareness || '',
      duration: step.duration || '10-15 seconds'
    });
    setShowStepEditor(true);
  };

  // Save movement step (create or update)
  const saveMovementStep = async () => {
    if (!stepEditorData.title.trim()) {
      toast.error('Please enter a step title');
      return;
    }
    
    setSavingStep(true);
    try {
      if (editingStep) {
        // Update existing
        await api.patch(`/movement-steps/${editingStep}`, {
          title: stepEditorData.title,
          description: stepEditorData.description,
          awareness: stepEditorData.awareness,
          duration: stepEditorData.duration
        });
        toast.success('Movement step updated!');
      } else {
        // Create new
        await api.post('/movement-steps', {
          submission_id: submissionId,
          from_area_id: stepEditorData.from_area_id,
          to_area_id: stepEditorData.to_area_id,
          title: stepEditorData.title,
          description: stepEditorData.description,
          awareness: stepEditorData.awareness,
          duration: stepEditorData.duration
        });
        toast.success('Movement step added!');
      }
      
      // Refresh movement steps
      const stepsResponse = await api.get(`/movement-steps/submission/${submissionId}`);
      setMovementSteps(stepsResponse.data || []);
      setShowStepEditor(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save movement step');
    } finally {
      setSavingStep(false);
    }
  };

  // Delete movement step
  const deleteMovementStep = async (stepId) => {
    if (!window.confirm('Delete this movement step?')) return;
    
    try {
      await api.delete(`/movement-steps/${stepId}`);
      toast.success('Movement step deleted');
      
      // Refresh movement steps
      const stepsResponse = await api.get(`/movement-steps/submission/${submissionId}`);
      setMovementSteps(stepsResponse.data || []);
    } catch (err) {
      toast.error('Failed to delete movement step');
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const response = await api.post(`/submissions/${submissionId}/generate-guide?regenerate=true`);
      setGuide(response.data);
      toast.success('Guide regenerated successfully!');
    } catch (err) {
      toast.error('Failed to regenerate guide');
    } finally {
      setRegenerating(false);
    }
  };

  // Navigation with history
  const goToScreen = (screen, area = null) => {
    if (area) setSelectedArea(area);
    setScreenHistory(prev => [...prev, screen]);
    setCurrentScreen(screen);
  };

  const goBack = () => {
    if (screenHistory.length > 1) {
      const newHistory = [...screenHistory];
      newHistory.pop();
      setScreenHistory(newHistory);
      setCurrentScreen(newHistory[newHistory.length - 1]);
    } else {
      setCurrentScreen(SCREENS.ENTRY);
    }
  };

  // ==================== HUMAN TRANSLATION HELPERS ====================
  
  // Convert numeric score to human label
  const getScoreLabel = (score) => {
    if (score <= 2) return 'Low';
    if (score === 3) return 'Moderate';
    return 'High';
  };

  // Get color for score
  const getScoreColor = (score) => {
    if (score <= 2) return '#22c55e'; // emerald
    if (score === 3) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  // Get background color class for score
  const getScoreBgClass = (score) => {
    if (score <= 2) return 'bg-emerald-500/20 border-emerald-400/30';
    if (score === 3) return 'bg-amber-500/20 border-amber-400/30';
    return 'bg-red-500/20 border-red-400/30';
  };

  // Get explanation for sensory category
  const getCategoryExplanation = (category, score) => {
    const label = getScoreLabel(score);
    const lower = category?.toLowerCase() || '';
    
    const explanations = {
      'noise': {
        'Low': 'Generally quiet with minimal background noise.',
        'Moderate': 'Noticeable background noise, especially at busy times.',
        'High': 'Can be loud, particularly during peak hours.'
      },
      'sound': {
        'Low': 'Generally quiet with minimal background noise.',
        'Moderate': 'Noticeable background noise, especially at busy times.',
        'High': 'Can be loud, particularly during peak hours.'
      },
      'light': {
        'Low': 'Soft, natural lighting with no harsh glare.',
        'Moderate': 'Mixed lighting that varies throughout the space.',
        'High': 'Bright or variable lighting that may be intense.'
      },
      'crowd': {
        'Low': 'Usually uncrowded with plenty of personal space.',
        'Moderate': 'Can get busy at peak times but generally manageable.',
        'High': 'Often crowded with limited personal space.'
      },
      'movement': {
        'Low': 'Calm environment with minimal activity around you.',
        'Moderate': 'Some movement and activity to be aware of.',
        'High': 'Busy with lots of people and activity.'
      },
      'smell': {
        'Low': 'Neutral or minimal scents in this area.',
        'Moderate': 'Some noticeable scents may be present.',
        'High': 'Strong or varied scents are common.'
      },
      'temp': {
        'Low': 'Comfortable, consistent temperature.',
        'Moderate': 'Temperature may vary in different parts.',
        'High': 'Temperature can be notably warm or cool.'
      },
      'visual': {
        'Low': 'Simple, calm visual environment.',
        'Moderate': 'Some visual activity and information.',
        'High': 'Visually busy with lots to take in.'
      },
      'enclosed': {
        'Low': 'Open and spacious feeling.',
        'Moderate': 'Mix of open and enclosed areas.',
        'High': 'May feel enclosed or confined.'
      },
      'space': {
        'Low': 'Plenty of room to move around.',
        'Moderate': 'Adequate space in most areas.',
        'High': 'Space may feel limited at times.'
      },
      'waiting': {
        'Low': 'Usually no waiting required.',
        'Moderate': 'Some waiting may be needed at busy times.',
        'High': 'Queues and waiting are common.'
      }
    };

    // Find matching category
    for (const [key, values] of Object.entries(explanations)) {
      if (lower.includes(key)) {
        return values[label] || 'Information varies based on conditions.';
      }
    }
    
    return label === 'Low' 
      ? 'Generally calm and comfortable.'
      : label === 'Moderate'
        ? 'May require some adjustment.'
        : 'May be challenging for some visitors.';
  };

  // Get area summary based on overall score
  const getAreaSummary = (categories) => {
    if (!categories || categories.length === 0) return null;
    
    let totalScore = 0;
    categories.forEach(cat => {
      const score = cat.traffic_light === 'green' ? 2 : cat.traffic_light === 'amber' ? 3 : 4;
      totalScore += score;
    });
    
    const avgScore = totalScore / categories.length;
    
    if (avgScore <= 2.3) {
      return {
        label: 'Low sensory environment',
        summary: 'This area is generally calm and easy to navigate.',
        tip: 'A good choice if you prefer quieter spaces.'
      };
    } else if (avgScore <= 3.3) {
      return {
        label: 'Moderate sensory environment',
        summary: 'This area may feel busy at times but is usually manageable.',
        tip: 'Best visited during quieter periods if sensitive to stimulation.'
      };
    } else {
      return {
        label: 'Higher sensory environment',
        summary: 'This area may feel overwhelming, especially during peak times.',
        tip: 'Consider visiting during off-peak hours or prepare for stimulation.'
      };
    }
  };

  // Traffic light helpers (keep original for API compatibility)
  const getTrafficLightColor = (trafficLight) => {
    switch (trafficLight) {
      case 'green': return '#22c55e';
      case 'amber': return '#f59e0b';
      case 'red': return '#ef4444';
      default: return '#94a3b8';
    }
  };

  const getTrafficLightLabel = (trafficLight) => {
    switch (trafficLight) {
      case 'green': return 'Low';
      case 'amber': return 'Moderate';
      case 'red': return 'High';
      default: return 'Not rated';
    }
  };

  const getCategoryIcon = (category) => {
    const lower = category?.toLowerCase() || '';
    if (lower.includes('noise') || lower.includes('sound')) return Volume2;
    if (lower.includes('light')) return Sun;
    if (lower.includes('crowd') || lower.includes('movement') || lower.includes('space')) return Users;
    if (lower.includes('smell')) return Wind;
    if (lower.includes('temp') || lower.includes('weather')) return Thermometer;
    if (lower.includes('visual')) return Eye;
    if (lower.includes('spatial') || lower.includes('layout') || lower.includes('enclosed') || lower.includes('waiting')) return Box;
    return Info;
  };

  // Get full area data by merging guide sensory data with original area data
  const getFullAreaData = (areaId) => {
    const sensoryArea = guide?.sensory_summary?.areas?.find(a => a.area_id === areaId);
    const originalArea = areas.find(a => a.area_id === areaId);
    return { ...originalArea, ...sensoryArea };
  };

  // ==================== TIER 1: ROUTE GENERATION HELPERS ====================
  
  // TRANSITION TYPES - Inferred from area names/descriptions
  const TRANSITION_TYPES = {
    corridor: { label: 'Through corridor', icon: ArrowRight, duration: '15-20 sec', description: 'Walk through the corridor' },
    corridor_short: { label: 'Short corridor', icon: ArrowRight, duration: '5-10 sec', description: 'Brief walk through corridor' },
    corridor_long: { label: 'Long corridor', icon: ArrowRight, duration: '30-45 sec', description: 'Walk through longer corridor' },
    open_space: { label: 'Open space', icon: Layers, duration: '10-15 sec', description: 'Cross through open area' },
    doors: { label: 'Through doors', icon: DoorOpen, duration: '5 sec', description: 'Pass through doors' },
    stairs: { label: 'Stairs', icon: ChevronUp, duration: '15-20 sec', description: 'Take the stairs' },
    ramp: { label: 'Ramp', icon: ArrowRight, duration: '10-15 sec', description: 'Walk up/down ramp' },
    narrow: { label: 'Narrow passage', icon: ArrowRight, duration: '5-10 sec', description: 'Move through narrow space' },
    default: { label: 'Moving between', icon: ArrowRight, duration: '10-15 sec', description: 'Continue walking' }
  };

  // Infer transition type from area names and descriptions
  const inferTransitionType = (fromArea, toArea) => {
    const fromName = (fromArea?.area_name || fromArea?.name || '').toLowerCase();
    const toName = (toArea?.area_name || toArea?.name || '').toLowerCase();
    const fromDesc = (fromArea?.description || fromArea?.observations || '').toLowerCase();
    const toDesc = (toArea?.description || toArea?.observations || '').toLowerCase();
    const combined = `${fromName} ${toName} ${fromDesc} ${toDesc}`;
    
    if (combined.includes('stair')) return 'stairs';
    if (combined.includes('ramp')) return 'ramp';
    if (combined.includes('narrow') || combined.includes('tight')) return 'narrow';
    if (combined.includes('door') || combined.includes('entry')) return 'doors';
    if (combined.includes('corridor') || combined.includes('hallway') || combined.includes('passage')) {
      if (combined.includes('long')) return 'corridor_long';
      if (combined.includes('short')) return 'corridor_short';
      return 'corridor';
    }
    if (combined.includes('open') || combined.includes('foyer') || combined.includes('lobby') || combined.includes('atrium')) return 'open_space';
    return 'default';
  };

  // Generate movement language for a step
  const getMovementText = (step, nextStep, isTransition = false) => {
    const name = step.area_name || step.name || 'this area';
    const nextName = nextStep?.area_name || nextStep?.name;
    
    if (isTransition) {
      const transType = TRANSITION_TYPES[step.transitionType] || TRANSITION_TYPES.default;
      return {
        primary: transType.description,
        secondary: step.stepType === 'transition' ? 'Moving between spaces' : null,
        progression: nextName ? `Next: ${transType.duration} to ${nextName}` : null
      };
    }
    
    // Generate primary action text based on step type
    let primary = '';
    if (step.stepType === 'start') {
      primary = `You arrive at ${name}.`;
    } else if (step.stepType === 'destination') {
      primary = `You reach ${name}.`;
    } else {
      primary = `You pass through ${name}.`;
    }
    
    // Generate feel text from sensory data
    let secondary = '';
    const categories = step.categories || [];
    const crowdCat = categories.find(c => (c.category || '').toLowerCase().includes('crowd'));
    const noiseCat = categories.find(c => (c.category || '').toLowerCase().includes('noise'));
    const spaceCat = categories.find(c => (c.category || '').toLowerCase().includes('space') || (c.category || '').toLowerCase().includes('spatial'));
    
    const crowdLevel = crowdCat?.traffic_light;
    const noiseLevel = noiseCat?.traffic_light;
    const spaceLevel = spaceCat?.traffic_light;
    
    if (crowdLevel === 'red' && noiseLevel === 'red') {
      secondary = 'Can feel busy and loud.';
    } else if (crowdLevel === 'red' || crowdLevel === 'amber') {
      secondary = 'May feel busy at times.';
    } else if (noiseLevel === 'red' || noiseLevel === 'amber') {
      secondary = 'Can be moderately noisy.';
    } else if (spaceLevel === 'red' || spaceLevel === 'amber') {
      secondary = 'May feel enclosed.';
    } else {
      secondary = 'Generally calm and manageable.';
    }
    
    // Progression to next
    let progression = null;
    if (nextStep) {
      const nextName = nextStep.area_name || nextStep.name;
      const transType = inferTransitionType(step, nextStep);
      const duration = TRANSITION_TYPES[transType]?.duration || '10-15 sec';
      progression = `Next: ${duration} to ${nextName}`;
    }
    
    return { primary, secondary, progression };
  };

  // Get all areas with full data merged
  const allAreasWithData = useMemo(() => {
    if (!guide?.sensory_summary?.areas) return [];
    return guide.sensory_summary.areas.map(sensoryArea => {
      const fullData = getFullAreaData(sensoryArea.area_id);
      // Get first image for card display
      const areaImage = fullData.photos?.[0]?.url || fullData.images?.[0]?.url || fullData.images?.[0] || fullData.image_url;
      // Ensure photos array is properly included for gallery
      const photosArray = fullData.photos || [];
      return {
        ...sensoryArea,
        ...fullData,
        image: areaImage,
        photos: photosArray, // Explicitly include photos array for ImageGallery
        trafficLight: sensoryArea.overall_traffic_light,
        label: getTrafficLightLabel(sensoryArea.overall_traffic_light)
      };
    });
  }, [guide, areas]);

  // Get the currently selected area for detail panel
  const selectedAreaData = useMemo(() => {
    if (!selectedAreaId) return allAreasWithData[0] || null;
    return allAreasWithData.find(a => a.area_id === selectedAreaId) || allAreasWithData[0] || null;
  }, [selectedAreaId, allAreasWithData]);

  // ==================== REAL-TIME STATUS ENGINE V2 (Owner-Controlled) ====================
  // Uses weekly busyness data set by venue owner, NOT generated from sensory data
  
  // Get current day and time block
  const getCurrentTimeInfo = useCallback(() => {
    const now = new Date();
    const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = days[now.getDay()];
    const hour = now.getHours();
    
    // Time blocks: Morning 06:00-11:59, Afternoon 12:00-16:59, Evening 17:00-23:59
    let timeBlock;
    if (hour >= 6 && hour < 12) {
      timeBlock = 'morning';
    } else if (hour >= 12 && hour < 17) {
      timeBlock = 'afternoon';
    } else if (hour >= 17 && hour < 24) {
      timeBlock = 'evening';
    } else {
      timeBlock = 'morning'; // 00:00-05:59 defaults to morning (closed hours)
    }
    
    return { currentDay, timeBlock, hour };
  }, []);
  
  // Calculate real-time status from weekly busyness data
  const venueStatus = useMemo(() => {
    const { currentDay, timeBlock } = getCurrentTimeInfo();
    
    // Check if weekly busyness data exists for current time
    const currentBusyness = weeklyBusyness?.[currentDay]?.[timeBlock];
    
    // Status mapping: busyness level → display values
    const STATUS_CONFIG = {
      quiet: {
        status: 'calm',
        label: 'Calm',
        color: '#22c55e',
        colorLight: '#22c55e15',
        summary: 'Generally calm with low noise and movement.',
        reassurance: 'You should feel comfortable here.'
      },
      moderate: {
        status: 'moderate',
        label: 'Moderate',
        color: '#f59e0b',
        colorLight: '#f59e0b15',
        summary: 'Can feel moderately busy with some noise.',
        reassurance: 'You can handle this.'
      },
      busy: {
        status: 'busy',
        label: 'Busy',
        color: '#f97316',
        colorLight: '#f9731615',
        summary: 'Can feel loud and crowded at this time.',
        reassurance: 'May feel overwhelming at peak times.'
      },
      very_busy: {
        status: 'intense',
        label: 'Intense',
        color: '#ef4444',
        colorLight: '#ef444415',
        summary: 'High crowd levels may feel overwhelming.',
        reassurance: 'Likely overwhelming for some visitors.'
      }
    };
    
    // If no data for current time, return "not set" state
    if (!currentBusyness) {
      return {
        status: 'not_set',
        label: 'Not set',
        color: '#94a3b8',
        colorLight: '#94a3b810',
        summary: 'Visitor levels not set for this time.',
        reassurance: 'Check with venue for current conditions.',
        hasData: false,
        currentDay,
        timeBlock
      };
    }
    
    const config = STATUS_CONFIG[currentBusyness] || STATUS_CONFIG.moderate;
    return {
      ...config,
      hasData: true,
      currentDay,
      timeBlock
    };
  }, [weeklyBusyness, getCurrentTimeInfo]);
  
  // Calculate "Best time to visit" from quiet blocks
  const bestTimeToVisit = useMemo(() => {
    if (!weeklyBusyness) return null;
    
    const quietTimes = [];
    const dayLabels = {
      monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday',
      thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday'
    };
    const timeLabels = { morning: 'mornings', afternoon: 'afternoons', evening: 'evenings' };
    
    Object.entries(weeklyBusyness).forEach(([day, blocks]) => {
      if (blocks) {
        Object.entries(blocks).forEach(([time, level]) => {
          if (level === 'quiet') {
            quietTimes.push(`${dayLabels[day]} ${timeLabels[time]}`);
          }
        });
      }
    });
    
    if (quietTimes.length === 0) return null;
    
    // Limit to 3 examples
    const display = quietTimes.slice(0, 3).join(', ');
    return `Usually quieter: ${display}`;
  }, [weeklyBusyness]);
  
  // Calculate days since busyness was last updated
  const busynessLastUpdated = useMemo(() => {
    if (!submission?.busyness_updated_at) return null;
    const updated = new Date(submission.busyness_updated_at);
    const now = new Date();
    const diffDays = Math.floor((now - updated) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Updated today';
    if (diffDays === 1) return 'Updated yesterday';
    return `Updated ${diffDays} days ago`;
  }, [submission?.busyness_updated_at]);
  
  // Save weekly busyness data
  const saveWeeklyBusyness = async () => {
    setSavingBusyness(true);
    try {
      await api.patch(`/submissions/${submissionId}/weekly-busyness`, {
        weekly_busyness: weeklyBusyness,
        busyness_notes: busynessNotes
      });
      setSubmission(prev => ({
        ...prev,
        weekly_busyness: weeklyBusyness,
        busyness_notes: busynessNotes,
        busyness_updated_at: new Date().toISOString()
      }));
      toast.success('Visitor levels saved!');
      setShowBusynessEditor(false);
    } catch (err) {
      toast.error('Failed to save visitor levels');
    } finally {
      setSavingBusyness(false);
    }
  };
  
  // Update a single time block in weekly busyness
  const updateBusynessBlock = (day, timeBlock, level) => {
    setWeeklyBusyness(prev => ({
      ...prev,
      [day]: {
        ...(prev?.[day] || {}),
        [timeBlock]: level
      }
    }));
  };

  // Generate MVP route to target area WITH TRANSITIONS
  // Uses shared routeBuilder utility for single source of truth
  const generateRouteToArea = (targetArea) => {
    const customRoutes = submission?.custom_routes || [];
    return buildRouteForDestination({
      allAreasWithData,
      movementSteps,
      customRoutes,
      targetArea
    });
  };

  // Get sensory insights for an area (for right panel)
  const getSensoryInsights = (area) => {
    if (!area?.categories) return [];
    
    const insights = [];
    area.categories.forEach(cat => {
      const catLower = (cat.category || '').toLowerCase();
      const score = cat.traffic_light === 'green' ? 2 : cat.traffic_light === 'amber' ? 3 : 4;
      
      if (catLower.includes('noise') || catLower.includes('sound')) {
        if (score >= 3) insights.push({ icon: Volume2, text: 'Can feel loud', color: getScoreColor(score) });
      }
      if (catLower.includes('crowd') || catLower.includes('movement')) {
        if (score >= 3) insights.push({ icon: Users, text: 'Busy after school', color: getScoreColor(score) });
        if (score <= 2) insights.push({ icon: Users, text: 'Usually quiet', color: getScoreColor(score) });
      }
      if (catLower.includes('light')) {
        if (score >= 4) insights.push({ icon: Sun, text: 'Bright lighting', color: getScoreColor(score) });
      }
      if (catLower.includes('enclosed') || catLower.includes('space')) {
        if (score >= 3) insights.push({ icon: Box, text: 'Can feel enclosed', color: getScoreColor(score) });
      }
    });
    
    // Add a safe/open times insight
    if (insights.length < 3) {
      insights.push({ icon: Clock, text: 'Safe, open times', color: '#22c55e' });
    }
    
    return insights.slice(0, 3);
  };

  // Find quiet/exit options for route
  const getExitOptions = () => {
    const quietAreas = allAreasWithData.filter(a => {
      const avgScore = (a.categories || []).reduce((sum, c) => {
        return sum + (c.traffic_light === 'green' ? 2 : c.traffic_light === 'amber' ? 3 : 4);
      }, 0) / Math.max((a.categories || []).length, 1);
      return avgScore <= 2.5;
    });
    return quietAreas.slice(0, 2);
  };

  // Handle area selection in Explore Hub
  const handleAreaSelect = (area) => {
    setSelectedAreaId(area.area_id);
    setSelectedArea(area);
    setMobileDetailOpen(true);
  };

  // Handle "Preview Your Route" action
  const handlePreviewRoute = (area) => {
    setRouteTargetArea(area);
    setCurrentStepIndex(0); // Reset step index when starting new route
    goToScreen(SCREENS.PREVIEW_ROUTE, area);
  };

  // ==================== DESIGN BRIEF COLORS ====================
  const primaryColor = venue?.brand_color_1 || venue?.primary_color || '#0d9488';
  const secondaryColor = venue?.brand_color_2 || venue?.secondary_color || '#10b981';
  const logoUrl = venue?.logo_url;

  // Dynamic styles based on design brief
  const primaryButtonStyle = {
    backgroundColor: primaryColor,
    color: 'white'
  };

  const primaryIconStyle = {
    color: primaryColor
  };

  // ==================== LOADING STATE ====================
  if (loading) {
    return (
      <div 
        className="min-h-screen relative"
        style={{ 
          background: `linear-gradient(180deg, ${brandColor} 0%, #4a3f6b 25%, #7c6a8a 50%, #d4a574 75%, #e8c49a 100%)`
        }}
      >
        <div className="max-w-lg mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-8">
            <div className="h-8 w-20 bg-white/20 rounded animate-pulse"></div>
            <div className="h-8 w-8 bg-white/20 rounded-full animate-pulse"></div>
          </div>
          <div className="text-center mb-8">
            <div className="h-8 w-3/4 bg-white/20 rounded mx-auto mb-4 animate-pulse"></div>
            <div className="h-4 w-1/2 bg-white/10 rounded mx-auto animate-pulse"></div>
          </div>
          <div className="space-y-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </div>
      </div>
    );
  }

  // ==================== ERROR STATE ====================
  if (error) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center px-6"
        style={{ 
          background: `linear-gradient(180deg, ${brandColor} 0%, #4a3f6b 25%, #7c6a8a 50%, #d4a574 75%, #e8c49a 100%)`
        }}
      >
        <div className="bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl p-8 text-center max-w-sm">
          <AlertCircle className="w-12 h-12 text-white/60 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Something went wrong</h2>
          <p className="text-white/70 text-sm mb-6">{error}</p>
          <Button 
            onClick={fetchAllData}
            className="rounded-full bg-white/90 text-slate-800 hover:bg-white"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!guide) {
    return (
      <div 
        className="min-h-screen flex items-center justify-center p-4" 
        style={{ 
          background: `linear-gradient(180deg, ${brandColor} 0%, #4a3f6b 25%, #7c6a8a 50%, #d4a574 75%, #e8c49a 100%)`
        }}
      >
        <div className="text-center">
          <p className="text-white/80 mb-4">Experience guide not available</p>
          <Button onClick={() => navigate('/dashboard')} className="rounded-full bg-white/20 backdrop-blur-md">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Data extraction
  const venueName = venue?.name || guide.source_summary?.venue_name || 'Venue';
  const venueDescription = venue?.description || '';
  const venueAddress = venue?.address || '';
  const venueType = venue?.venue_type || guide.source_summary?.venue_type || '';
  const sensorySummary = guide.sensory_summary || {};

  // Glassmorphism styles (enhanced)
  const glassCard = "bg-white/15 backdrop-blur-2xl border border-white/20 shadow-2xl";
  const glassButton = "bg-white/85 backdrop-blur-xl border border-white/40 shadow-xl hover:bg-white/95 hover:shadow-2xl transition-all duration-300";

  // Screen indicator dots
  const screenOrder = [SCREENS.ENTRY, SCREENS.AREAS, SCREENS.ABOUT, SCREENS.SENSORY];
  const currentScreenIndex = screenOrder.indexOf(currentScreen);

  // Breadcrumb text
  const getBreadcrumb = () => {
    if (currentScreen === SCREENS.BREAKDOWN && selectedArea) {
      return `Explore Areas › ${selectedArea.area_name}`;
    }
    return null;
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{ 
        background: `linear-gradient(180deg, ${brandColor} 0%, #4a3f6b 25%, #7c6a8a 50%, #d4a574 75%, #e8c49a 100%)`
      }}
    >
      {/* Subtle primary color overlay */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{ 
          background: `linear-gradient(180deg, ${primaryColor}15 0%, transparent 50%)` 
        }}
      />

      {/* Main Container - Wider for Tier 1, narrower for legacy */}
      <div className={`relative mx-auto min-h-screen flex flex-col ${
        USE_TIER1_UI && (currentScreen === SCREENS.EXPLORE_HUB || currentScreen === SCREENS.PREVIEW_ROUTE)
          ? 'max-w-7xl'
          : 'max-w-lg'
      }`}>
        
        {/* ==================== HEADER (Legacy screens only) ==================== */}
        {!(currentScreen === SCREENS.EXPLORE_HUB || currentScreen === SCREENS.PREVIEW_ROUTE) && (
        <header className="relative z-50 px-6 pt-6 pb-4 flex items-center justify-between safe-area-top">
          {currentScreen === SCREENS.ENTRY ? (
            <>
              {/* Logo */}
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-8 object-contain" />
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-white/90 font-semibold text-lg tracking-wide">nysa</span>
                </div>
              )}
              
              {/* Menu Button */}
              <button 
                onClick={() => setMenuOpen(true)}
                className="p-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-colors"
              >
                <Menu className="w-5 h-5 text-white" />
              </button>
            </>
          ) : (
            <>
              {/* Back Button */}
              <button 
                onClick={goBack}
                className="flex items-center gap-2 text-white/90 hover:text-white transition-colors py-1"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="font-medium">Back</span>
              </button>
              
              {/* Breadcrumb */}
              {getBreadcrumb() && (
                <span className="text-white/50 text-xs truncate max-w-[150px]">
                  {getBreadcrumb()}
                </span>
              )}
            </>
          )}
        </header>
        )}

        {/* ==================== MENU OVERLAY ==================== */}
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
              onClick={() => setMenuOpen(false)}
            >
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25 }}
                className="absolute right-0 top-0 bottom-0 w-72 bg-white/95 backdrop-blur-xl p-6 safe-area-right"
                onClick={e => e.stopPropagation()}
              >
                <div className="flex justify-between items-center mb-8">
                  <span className="font-semibold text-slate-800">Menu</span>
                  <button onClick={() => setMenuOpen(false)} className="p-1">
                    <X className="w-5 h-5 text-slate-600" />
                  </button>
                </div>
                
                <div className="space-y-1">
                  {[
                    { screen: SCREENS.EXPLORE_HUB, label: 'Home' },
                    { screen: SCREENS.EXPLORE_HUB, label: 'Explore Areas' },
                    { screen: SCREENS.ABOUT, label: 'About the Venue' },
                    { screen: SCREENS.SENSORY, label: 'Sensory Overview' }
                  ].map((item, idx) => (
                    <button 
                      key={`${item.screen}-${idx}`}
                      onClick={() => { goToScreen(item.screen); setMenuOpen(false); }}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-colors ${
                        (currentScreen === item.screen || (item.label === 'Home' && currentScreen === SCREENS.EXPLORE_HUB))
                          ? 'bg-slate-100 text-slate-900 font-medium' 
                          : 'hover:bg-slate-50 text-slate-700'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                {/* Subscription CTA */}
                {!isSubscribed && (
                  <div 
                    className="mt-8 p-4 rounded-xl text-white"
                    style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                  >
                    <p className="text-sm font-medium mb-2">Unlock full features</p>
                    <Button size="sm" className="w-full bg-white hover:bg-white/90 rounded-full" style={{ color: primaryColor }}>
                      Start free trial
                    </Button>
                  </div>
                )}

                {/* Premium Actions */}
                <div className="mt-6 space-y-2">
                  <button 
                    disabled={!isSubscribed}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-slate-100 text-slate-600 disabled:opacity-50"
                  >
                    <span className="flex items-center gap-2">
                      <Download className="w-4 h-4" />
                      Download
                    </span>
                    {!isSubscribed && <Lock className="w-3 h-3" />}
                  </button>
                  <button 
                    disabled={!isSubscribed}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-slate-100 text-slate-600 disabled:opacity-50"
                  >
                    <span className="flex items-center gap-2">
                      <Share2 className="w-4 h-4" />
                      Share
                    </span>
                    {!isSubscribed && <Lock className="w-3 h-3" />}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ==================== SUBSCRIPTION BANNER ==================== */}
        {!isSubscribed && currentScreen === SCREENS.ENTRY && (
          <div className="mx-6 mb-4">
            <div className={`${glassCard} rounded-2xl p-3 flex items-center justify-between`}>
              <p className="text-white/90 text-xs font-medium">Unlock full features with a subscription</p>
              <Button 
                size="sm" 
                className="rounded-full text-xs px-3 h-7"
                style={primaryButtonStyle}
              >
                Free trial
              </Button>
            </div>
          </div>
        )}

        {/* ==================== CONTENT AREA ==================== */}
        <main className="flex-1 px-6 pb-8 safe-area-bottom">
          <AnimatePresence mode="wait">
            
            {/* ==================== SCREEN: ENTRY ==================== */}
            {currentScreen === SCREENS.ENTRY && (
              <motion.div
                key="entry"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
                className="flex flex-col items-center text-center pt-8"
              >
                {/* Venue Name */}
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight drop-shadow-lg">
                  {venueName}
                </h1>
                
                {/* Subtitle */}
                <p className="text-white/80 text-base mb-8">
                  Welcome to your sensory experience
                </p>

                {/* Overall Sensory Badge */}
                {sensorySummary.overall && (
                  <div className={`${glassCard} rounded-full px-5 py-2.5 mb-10 inline-flex items-center gap-2.5`}>
                    <span 
                      className="w-3.5 h-3.5 rounded-full shadow-sm"
                      style={{ backgroundColor: getTrafficLightColor(sensorySummary.overall.traffic_light) }}
                    />
                    <span className="text-white font-medium text-sm">
                      {getTrafficLightLabel(sensorySummary.overall.traffic_light)} sensory environment
                    </span>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="w-full space-y-4">
                  {/* Primary: Explore Areas */}
                  <button
                    onClick={() => goToScreen(SCREENS.AREAS)}
                    className={`${glassButton} w-full rounded-2xl p-4 flex items-center justify-between group`}
                    data-testid="nav-explore-areas"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg"
                        style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}
                      >
                        <MapPin className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-semibold text-slate-800">Explore Areas</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </button>

                  {/* About the Venue */}
                  <button
                    onClick={() => goToScreen(SCREENS.ABOUT)}
                    className={`${glassButton} w-full rounded-2xl p-4 flex items-center justify-between group`}
                    data-testid="nav-about-venue"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-slate-400 to-slate-600 flex items-center justify-center shadow-lg">
                        <Building2 className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-semibold text-slate-800">About the Venue</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </button>

                  {/* Sensory Overview */}
                  <button
                    onClick={() => goToScreen(SCREENS.SENSORY)}
                    className={`${glassButton} w-full rounded-2xl p-4 flex items-center justify-between group`}
                    data-testid="nav-sensory-overview"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg">
                        <Eye className="w-5 h-5 text-white" />
                      </div>
                      <span className="font-semibold text-slate-800">Sensory Overview</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* ==================== SCREEN: ABOUT ==================== */}
            {currentScreen === SCREENS.ABOUT && (
              <motion.div
                key="about"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-white text-center mb-6 drop-shadow-lg">About the Venue</h2>
                
                <div className={`${glassCard} rounded-3xl overflow-hidden`}>
                  {/* Address */}
                  {venueAddress ? (
                    <div className="p-5 border-b border-white/10">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-white/50 text-xs uppercase tracking-wider block mb-1">Address</span>
                          <p className="text-white">{venueAddress}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Venue Type */}
                  {venueType ? (
                    <div className="p-5 border-b border-white/10">
                      <div className="flex items-start gap-3">
                        <Building2 className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-white/50 text-xs uppercase tracking-wider block mb-1">Venue Type</span>
                          <p className="text-white">{venueType}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  
                  {/* Full Description - EXACT USER INPUT */}
                  {venueDescription ? (
                    <div className="p-5 border-b border-white/10">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="text-white/50 text-xs uppercase tracking-wider block mb-2">About</span>
                          <div className="text-white/90 leading-relaxed">
                            {venueDescription.length > 300 && !expandedAbout ? (
                              <>
                                <p>{venueDescription.substring(0, 300)}...</p>
                                <button 
                                  onClick={() => setExpandedAbout(true)}
                                  className="mt-2 text-sm font-medium flex items-center gap-1"
                                  style={{ color: primaryColor }}
                                >
                                  Read more <ChevronDown className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <>
                                <p>{venueDescription}</p>
                                {venueDescription.length > 300 && (
                                  <button 
                                    onClick={() => setExpandedAbout(false)}
                                    className="mt-2 text-sm font-medium flex items-center gap-1"
                                    style={{ color: primaryColor }}
                                  >
                                    Show less <ChevronUp className="w-4 h-4" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Entry Models */}
                  {venue?.entry_models?.length > 0 ? (
                    <div className="p-5 border-b border-white/10">
                      <div className="flex items-start gap-3">
                        <Ticket className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-white/50 text-xs uppercase tracking-wider block mb-1">Entry</span>
                          <p className="text-white">{venue.entry_models.join(', ')}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Structure */}
                  {venue?.structural_characteristics?.length > 0 ? (
                    <div className="p-5 border-b border-white/10">
                      <div className="flex items-start gap-3">
                        <Layers className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-white/50 text-xs uppercase tracking-wider block mb-1">Structure</span>
                          <p className="text-white">{venue.structural_characteristics.join(', ')}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Environment */}
                  {venue?.primary_environment ? (
                    <div className="p-5">
                      <div className="flex items-start gap-3">
                        <Sun className="w-5 h-5 text-white/60 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="text-white/50 text-xs uppercase tracking-wider block mb-1">Environment</span>
                          <p className="text-white">{venue.primary_environment}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  {/* Fallback if no data */}
                  {!venueAddress && !venueType && !venueDescription && (
                    <div className="p-8 text-center">
                      <Info className="w-8 h-8 text-white/30 mx-auto mb-2" />
                      <p className="text-white/50 text-sm">Information not provided</p>
                    </div>
                  )}
                </div>
              </motion.div>
            )}

            {/* ==================== SCREEN: SENSORY OVERVIEW ==================== */}
            {currentScreen === SCREENS.SENSORY && (
              <motion.div
                key="sensory"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-white text-center mb-6 drop-shadow-lg">Sensory Overview</h2>
                
                {/* Area Count */}
                <div className={`${glassCard} rounded-3xl p-6 mb-4 text-center`}>
                  <span className="text-5xl font-bold text-white">{sensorySummary.areas?.length || 0}</span>
                  <p className="text-white/70 mt-1 font-medium">Areas Assessed</p>
                </div>

                {/* Traffic Light Distribution */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className={`${glassCard} rounded-2xl p-4 text-center`}>
                    <div className="w-4 h-4 rounded-full bg-emerald-500 mx-auto mb-2 shadow-lg" />
                    <span className="text-2xl font-bold text-white">{sensorySummary.distribution?.green || 0}</span>
                    <p className="text-white/70 text-xs mt-1 font-medium">Low</p>
                  </div>
                  <div className={`${glassCard} rounded-2xl p-4 text-center`}>
                    <div className="w-4 h-4 rounded-full bg-amber-500 mx-auto mb-2 shadow-lg" />
                    <span className="text-2xl font-bold text-white">{sensorySummary.distribution?.amber || 0}</span>
                    <p className="text-white/70 text-xs mt-1 font-medium">Moderate</p>
                  </div>
                  <div className={`${glassCard} rounded-2xl p-4 text-center`}>
                    <div className="w-4 h-4 rounded-full bg-red-500 mx-auto mb-2 shadow-lg" />
                    <span className="text-2xl font-bold text-white">{sensorySummary.distribution?.red || 0}</span>
                    <p className="text-white/70 text-xs mt-1 font-medium">High</p>
                  </div>
                </div>

                {/* Human-Readable Explanation */}
                <div className={`${glassCard} rounded-3xl p-5`}>
                  <p className="text-white/90 leading-relaxed text-sm">
                    This venue has <strong className="text-white">{sensorySummary.areas?.length || 0} areas</strong> that have been assessed. 
                    Across all areas, <strong className="text-emerald-400">{sensorySummary.distribution?.green || 0}</strong> sensory factors are rated low, 
                    <strong className="text-amber-400"> {sensorySummary.distribution?.amber || 0}</strong> moderate, and 
                    <strong className="text-red-400"> {sensorySummary.distribution?.red || 0}</strong> high.
                  </p>
                </div>

                {/* CTA */}
                <button
                  onClick={() => goToScreen(SCREENS.AREAS)}
                  className="w-full rounded-2xl p-4 mt-6 flex items-center justify-center gap-2 font-semibold shadow-xl"
                  style={primaryButtonStyle}
                >
                  <MapPin className="w-5 h-5" />
                  Explore All Areas
                </button>
              </motion.div>
            )}

            {/* ==================== SCREEN: EXPLORE AREAS ==================== */}
            {currentScreen === SCREENS.AREAS && (
              <motion.div
                key="areas"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-white text-center mb-6 drop-shadow-lg">Explore Areas</h2>
                
                <div className="space-y-4">
                  {sensorySummary.areas?.map((sensoryArea, index) => {
                    const fullArea = getFullAreaData(sensoryArea.area_id);
                    // Photos are stored as [{url, order}] objects, need to access .url
                    const areaImage = fullArea.photos?.[0]?.url || fullArea.images?.[0]?.url || fullArea.images?.[0] || fullArea.image_url;
                    const areaDescription = fullArea.description || '';
                    const areaSummary = getAreaSummary(sensoryArea.categories);

                    return (
                      <motion.div
                        key={sensoryArea.area_id || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`${glassCard} rounded-3xl overflow-hidden`}
                        data-testid={`area-card-${sensoryArea.area_id}`}
                      >
                        {/* Area Image with Fallback */}
                        <AreaImage 
                          src={areaImage} 
                          alt={sensoryArea.area_name}
                          className="aspect-video"
                        />
                        
                        {/* Area Content */}
                        <div className="p-5">
                          {/* Name and Badge */}
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2.5">
                              <span 
                                className="w-3.5 h-3.5 rounded-full flex-shrink-0 shadow-sm"
                                style={{ backgroundColor: getTrafficLightColor(sensoryArea.overall_traffic_light) }}
                              />
                              <h3 className="font-bold text-white text-lg">{sensoryArea.area_name}</h3>
                            </div>
                          </div>
                          
                          {/* Human-Readable Sensory Label */}
                          <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 mb-3">
                            <span className="text-white/90 text-xs font-medium">
                              {getTrafficLightLabel(sensoryArea.overall_traffic_light)} sensory environment
                            </span>
                          </div>
                          
                          {/* Area Summary */}
                          {areaSummary && (
                            <p className="text-white/70 text-sm mb-3">
                              {areaSummary.summary}
                            </p>
                          )}
                          
                          {/* Full Description - EXACT USER INPUT */}
                          {areaDescription && (
                            <p className="text-white/80 text-sm leading-relaxed mb-4">
                              {areaDescription}
                            </p>
                          )}
                          
                          {/* Primary CTA */}
                          <button
                            onClick={() => goToScreen(SCREENS.BREAKDOWN, sensoryArea)}
                            className="w-full rounded-xl py-3 px-4 flex items-center justify-center gap-2 font-medium shadow-lg transition-all"
                            style={primaryButtonStyle}
                          >
                            View Sensory Breakdown
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* ==================== SCREEN: AREA BREAKDOWN ==================== */}
            {currentScreen === SCREENS.BREAKDOWN && selectedArea && (
              <motion.div
                key="breakdown"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                {(() => {
                  const fullArea = getFullAreaData(selectedArea.area_id);
                  // Photos are stored as [{url, order}] objects, need to access .url
                  const areaImage = fullArea.photos?.[0]?.url || fullArea.images?.[0]?.url || fullArea.images?.[0] || fullArea.image_url;
                  const areaSummary = getAreaSummary(selectedArea.categories);

                  return (
                    <>
                      {/* Area Image */}
                      <AreaImage 
                        src={areaImage} 
                        alt={selectedArea.area_name}
                        className="aspect-video rounded-3xl overflow-hidden mb-6"
                      />

                      {/* Area Name */}
                      <h2 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">{selectedArea.area_name}</h2>
                      
                      {/* Human Summary Card */}
                      {areaSummary && (
                        <div 
                          className={`${glassCard} rounded-2xl p-5 mb-6`}
                          style={{ borderColor: getTrafficLightColor(selectedArea.overall_traffic_light) + '40' }}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <span 
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: getTrafficLightColor(selectedArea.overall_traffic_light) }}
                            />
                            <span className="text-white font-semibold">{areaSummary.label}</span>
                          </div>
                          <p className="text-white/80 text-sm leading-relaxed mb-2">
                            {areaSummary.summary}
                          </p>
                          <p className="text-white/60 text-xs italic">
                            {areaSummary.tip}
                          </p>
                        </div>
                      )}

                      {/* Sensory Factors Header with Help */}
                      <div className="flex items-center justify-between mb-3 relative">
                        <h3 className="text-white/70 text-sm uppercase tracking-wider font-medium">Sensory Factors</h3>
                        <button 
                          onClick={() => setShowSensoryHelp(!showSensoryHelp)}
                          className="flex items-center gap-1 text-white/50 text-xs hover:text-white/70 transition-colors"
                        >
                          <HelpCircle className="w-3.5 h-3.5" />
                          What does this mean?
                        </button>
                        <SensoryHelpTooltip isOpen={showSensoryHelp} onClose={() => setShowSensoryHelp(false)} />
                      </div>
                      
                      {/* Sensory Factors List */}
                      <div className="space-y-3">
                        {selectedArea.categories?.map((cat, index) => {
                          const Icon = getCategoryIcon(cat.category);
                          const score = cat.traffic_light === 'green' ? 2 : cat.traffic_light === 'amber' ? 3 : 4;
                          const label = getScoreLabel(score);
                          const explanation = getCategoryExplanation(cat.category, score);
                          const scoreColor = getScoreColor(score);
                          
                          return (
                            <div
                              key={index}
                              className={`${glassCard} rounded-2xl p-4`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Icon - uses sensory colors */}
                                <div 
                                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                                  style={{ backgroundColor: `${scoreColor}20` }}
                                >
                                  <Icon className="w-5 h-5" style={{ color: scoreColor }} />
                                </div>
                                
                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-white font-medium">{cat.category}</span>
                                    {/* Primary: Human Label */}
                                    <span 
                                      className="text-sm font-semibold px-2 py-0.5 rounded-full"
                                      style={{ 
                                        backgroundColor: `${scoreColor}20`,
                                        color: scoreColor 
                                      }}
                                    >
                                      {label}
                                    </span>
                                  </div>
                                  {/* Human Explanation */}
                                  <p className="text-white/60 text-sm leading-relaxed">
                                    {explanation}
                                  </p>
                                  {/* Secondary: Numeric Score */}
                                  <span className="text-white/40 text-xs mt-1 block">
                                    Score: {score} / 5
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Premium Actions */}
                      {selectedArea.categories?.length > 0 && (
                        <div className="mt-6 grid grid-cols-2 gap-3">
                          <button 
                            disabled={!isSubscribed}
                            className={`${glassCard} rounded-xl p-3 flex items-center justify-center gap-2 text-white/80 text-sm font-medium disabled:opacity-50`}
                          >
                            <Download className="w-4 h-4" />
                            Download
                            {!isSubscribed && <Lock className="w-3 h-3 ml-1" />}
                          </button>
                          <button 
                            disabled={!isSubscribed}
                            className={`${glassCard} rounded-xl p-3 flex items-center justify-center gap-2 text-white/80 text-sm font-medium disabled:opacity-50`}
                          >
                            <Share2 className="w-4 h-4" />
                            Share
                            {!isSubscribed && <Lock className="w-3 h-3 ml-1" />}
                          </button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </motion.div>
            )}

            {/* ==================== TIER 1: EXPLORE HUB ==================== */}
            {currentScreen === SCREENS.EXPLORE_HUB && (
              <motion.div
                key="explore-hub"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full pb-20"
              >
                {/* TIER 1 HEADER */}
                <div className="bg-[#1a365d] -mx-6 -mt-4 px-4 py-3 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="h-7 object-contain" />
                    ) : (
                      <span className="text-white font-bold text-lg tracking-wide">nysa</span>
                    )}
                    <span className="text-white/90 text-sm font-medium hidden sm:block">{venueName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Share Button - copies PUBLIC URL */}
                    <button 
                      onClick={() => {
                        const publicUrl = `${window.location.origin}/experience/${submissionId}`;
                        navigator.clipboard.writeText(publicUrl).then(() => {
                          toast.success('Public link copied! Share this with visitors.');
                        }).catch(() => {
                          toast.error('Failed to copy link');
                        });
                      }}
                      className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                      data-testid="share-guide-btn"
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      <span>Share</span>
                    </button>
                    <button 
                      onClick={() => setMenuOpen(true)}
                      className="p-2 text-white/70 hover:text-white"
                    >
                      <Menu className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* ========== REAL-TIME STATUS V2: OWNER-CONTROLLED ========== */}
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="mb-4"
                  data-testid="venue-status-section"
                >
                  <div 
                    className="backdrop-blur-xl rounded-2xl p-5 border"
                    style={{ 
                      backgroundColor: venueStatus.colorLight,
                      borderColor: `${venueStatus.color}30`
                    }}
                  >
                    {/* Status Header Row */}
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="flex items-center gap-3">
                        {/* Status Indicator */}
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${venueStatus.color}25` }}
                        >
                          {venueStatus.status === 'calm' && <Shield className="w-6 h-6" style={{ color: venueStatus.color }} />}
                          {venueStatus.status === 'moderate' && <Clock className="w-6 h-6" style={{ color: venueStatus.color }} />}
                          {venueStatus.status === 'busy' && <Users className="w-6 h-6" style={{ color: venueStatus.color }} />}
                          {venueStatus.status === 'intense' && <AlertCircle className="w-6 h-6" style={{ color: venueStatus.color }} />}
                          {venueStatus.status === 'not_set' && <HelpCircle className="w-6 h-6" style={{ color: venueStatus.color }} />}
                        </div>
                        
                        {/* Primary Status Text */}
                        <div>
                          <div className="flex items-center gap-2 mb-0.5">
                            <span 
                              className="text-lg font-bold"
                              style={{ color: venueStatus.color }}
                              data-testid="venue-status-label"
                            >
                              {venueStatus.label}
                            </span>
                            {venueStatus.hasData && (
                              <span className="text-white/60 text-sm">right now</span>
                            )}
                          </div>
                          <p className="text-white/80 text-sm leading-relaxed" data-testid="venue-status-summary">
                            {venueStatus.summary}
                          </p>
                        </div>
                      </div>
                      
                      {/* Edit button (owner only) */}
                      {isOwner && (
                        <button
                          onClick={() => setShowBusynessEditor(true)}
                          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors"
                          data-testid="edit-busyness-btn"
                          title="Edit visitor levels"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Only show additional info if data exists */}
                    {(bestTimeToVisit || busynessLastUpdated) && (
                      <>
                        <div className="h-px bg-white/10 my-3" />
                        
                        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                          {/* Best Time to Visit - ONLY if quiet blocks exist */}
                          {bestTimeToVisit && (
                            <div className="flex items-start gap-2 flex-1">
                              <Clock className="w-4 h-4 text-white/50 mt-0.5 flex-shrink-0" />
                              <div>
                                <span className="text-white/50 text-xs uppercase tracking-wider block mb-0.5">Best time to visit</span>
                                <span className="text-white/80 text-sm" data-testid="venue-best-time">{bestTimeToVisit}</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Reassurance + Trust Indicator */}
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <div 
                              className="flex items-center gap-2 px-3 py-2 rounded-xl"
                              style={{ backgroundColor: `${venueStatus.color}15` }}
                            >
                              <Heart className="w-4 h-4 flex-shrink-0" style={{ color: venueStatus.color }} />
                              <span 
                                className="text-sm font-medium"
                                style={{ color: venueStatus.color }}
                                data-testid="venue-reassurance"
                              >
                                {venueStatus.reassurance}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Trust Indicator */}
                        {busynessLastUpdated && (
                          <div className="mt-3 text-white/40 text-xs flex items-center gap-1">
                            <Check className="w-3 h-3" />
                            <span data-testid="busyness-updated">{busynessLastUpdated}</span>
                          </div>
                        )}
                      </>
                    )}
                    
                    {/* Notes from owner */}
                    {busynessNotes && (
                      <div className="mt-3 pt-3 border-t border-white/10">
                        <p className="text-white/60 text-xs italic">"{busynessNotes}"</p>
                      </div>
                    )}
                  </div>
                </motion.div>

                {/* ========== FIRST IMPRESSION / ENTRY SECTION ========== */}
                <div 
                  className="backdrop-blur-xl rounded-2xl p-5 mb-6 border border-white/10"
                  style={{ backgroundColor: '#1e3a5fe6' }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    {/* Venue Info */}
                    <div className="flex-1">
                      <h1 className="text-2xl font-bold text-white mb-1" data-testid="venue-name">{venueName}</h1>
                      
                      {/* Address - Always visible if available */}
                      {venueAddress && (
                        <div className="flex items-center gap-2 text-white/70 text-sm mb-2" data-testid="venue-address">
                          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                          <span>{venueAddress}</span>
                        </div>
                      )}
                      
                      {/* Overall Sensory Indicator */}
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: getTrafficLightColor(sensorySummary.overall?.traffic_light) }}
                          />
                          <span className="text-white/90 text-sm font-medium">
                            {getTrafficLightLabel(sensorySummary.overall?.traffic_light)} sensory environment overall
                          </span>
                        </div>
                        <span className="text-white/50 text-xs">
                          {allAreasWithData.length} areas
                        </span>
                      </div>
                    </div>
                    {/* CTA */}
                    <button
                      onClick={() => {
                        const firstArea = allAreasWithData[0];
                        if (firstArea) handleAreaSelect(firstArea);
                      }}
                      className="flex-shrink-0 bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors flex items-center gap-2"
                    >
                      <MapPin className="w-4 h-4" />
                      Explore the venue
                    </button>
                  </div>
                  
                  {/* About Section - Always visible */}
                  {venueDescription && venueDescription.length > 50 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <span className="text-white/60 text-xs uppercase tracking-wider font-medium block mb-2">About this venue</span>
                      <p className="text-white/70 text-sm leading-relaxed" data-testid="venue-about-text">
                        {venueDescription}
                      </p>
                    </div>
                  )}
                  
                  {/* Venue Context - Collapsible dropdown */}
                  {(venue?.venue_type || venue?.venue_size || venue?.structural_characteristics?.length > 0 || venue?.entry_models?.length > 0) && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <button
                        onClick={() => setShowVenueAbout(!showVenueAbout)}
                        className="flex items-center justify-between w-full text-left group"
                        data-testid="venue-context-toggle"
                      >
                        <span className="text-white/60 text-xs uppercase tracking-wider font-medium">Venue Context</span>
                        <ChevronDown className={`w-4 h-4 text-white/40 transition-transform ${showVenueAbout ? 'rotate-180' : ''}`} />
                      </button>
                      <AnimatePresence>
                        {showVenueAbout && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-2 mt-3">
                              {venue.venue_type && (
                                <div className="flex items-start gap-2">
                                  <span className="text-white/50 text-xs min-w-[80px]">Type:</span>
                                  <span className="text-white/80 text-sm">{venue.venue_type}</span>
                                </div>
                              )}
                              
                              {venue.venue_size && (
                                <div className="flex items-start gap-2">
                                  <span className="text-white/50 text-xs min-w-[80px]">Size:</span>
                                  <span className="text-white/80 text-sm">{venue.venue_size}</span>
                                </div>
                              )}
                              
                              {venue.entry_models?.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <span className="text-white/50 text-xs min-w-[80px]">Entry:</span>
                                  <span className="text-white/80 text-sm">{venue.entry_models.join(', ')}</span>
                                </div>
                              )}
                              
                              {venue.structural_characteristics?.length > 0 && (
                                <div className="flex items-start gap-2">
                                  <span className="text-white/50 text-xs min-w-[80px]">Layout:</span>
                                  <span className="text-white/80 text-sm">{venue.structural_characteristics.join(', ')}</span>
                                </div>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* MAIN CONTENT - 3 COLUMN LAYOUT ON DESKTOP */}
                <div className="flex flex-col lg:flex-row gap-4 h-full">
                  
                  {/* LEFT PANEL - Area Navigation List */}
                  <div className="lg:w-64 flex-shrink-0">
                    <div 
                      className="backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10"
                      style={{ backgroundColor: '#1e3a5fe6' }}
                    >
                      <div className="px-4 py-3 border-b border-white/10">
                        <h2 className="text-white/90 text-xs font-semibold uppercase tracking-wider">Experience Hub</h2>
                      </div>
                      <div className="max-h-[50vh] lg:max-h-[60vh] overflow-y-auto">
                        {allAreasWithData.map((area, index) => (
                          <button
                            key={area.area_id || index}
                            onClick={() => handleAreaSelect(area)}
                            data-testid={`hub-area-nav-${area.area_id}`}
                            className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-all border-b border-white/5 last:border-0 ${
                              selectedAreaId === area.area_id 
                                ? 'bg-white/15' 
                                : 'hover:bg-white/10'
                            }`}
                          >
                            <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                              selectedAreaId === area.area_id ? 'bg-white/20' : ''
                            }`}>
                              {selectedAreaId === area.area_id ? (
                                <ChevronRight className="w-3 h-3 text-white" />
                              ) : (
                                <Check className="w-3 h-3 text-white/50" />
                              )}
                            </span>
                            <span className="flex-1 text-white text-sm truncate">{area.area_name || area.name}</span>
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-medium"
                              style={{ 
                                backgroundColor: `${getTrafficLightColor(area.trafficLight)}20`,
                                color: getTrafficLightColor(area.trafficLight)
                              }}
                            >
                              {area.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* MIDDLE PANEL - Area Cards Grid */}
                  <div className="flex-1 min-w-0">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {allAreasWithData.map((area, index) => {
                        // Generate short sensory summary for card
                        const areaSummary = getAreaSummary(area.categories);
                        const shortSummary = areaSummary?.summary?.split('.')[0] || 'Tap to explore';
                        
                        return (
                          <motion.div
                            key={area.area_id || index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            onClick={() => handleAreaSelect(area)}
                            data-testid={`hub-area-card-${area.area_id}`}
                            className={`bg-white/10 backdrop-blur-xl rounded-2xl overflow-hidden cursor-pointer transition-all hover:bg-white/15 border-2 ${
                              selectedAreaId === area.area_id 
                                ? 'border-white/40 ring-2 ring-white/20' 
                                : 'border-transparent'
                            }`}
                          >
                            {/* Area Image - First image only on card */}
                            <AreaImage 
                              src={area.image} 
                              alt={area.area_name || area.name}
                              className="aspect-[16/10]"
                            />
                            
                            {/* SIMPLIFIED Card Content: name + label + short summary only */}
                            <div className="p-3">
                              {/* Header with name and sensory label */}
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-white font-semibold text-sm truncate flex-1">{area.area_name || area.name}</span>
                                <span 
                                  className="px-2 py-0.5 rounded text-xs font-bold flex-shrink-0 ml-2"
                                  style={{ 
                                    backgroundColor: getTrafficLightColor(area.trafficLight),
                                    color: 'white'
                                  }}
                                >
                                  {area.label}
                                </span>
                              </div>
                              
                              {/* Short sensory summary (1-2 lines max) */}
                              <p className="text-white/60 text-xs line-clamp-1">
                                {shortSummary}
                              </p>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  </div>

                  {/* RIGHT PANEL - Selected Area Detail (Desktop) */}
                  <div className="hidden lg:block lg:w-80 flex-shrink-0">
                    {selectedAreaData && (
                      <div 
                        className="backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 sticky top-4"
                        style={{ backgroundColor: '#1e3a5fe6' }}
                      >
                        {/* Area Header */}
                        <div className="bg-[#1a365d] px-4 py-3 flex items-center justify-between">
                          <span className="text-white font-semibold">{selectedAreaData.area_name || selectedAreaData.name}</span>
                          <span 
                            className="px-2 py-0.5 rounded text-xs font-bold"
                            style={{ 
                              backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight),
                              color: 'white'
                            }}
                          >
                            {selectedAreaData.label}
                          </span>
                        </div>
                        
                        {/* Area Images - Multi-image gallery support */}
                        <ImageGallery 
                          photos={selectedAreaData.photos} 
                          areaName={selectedAreaData.area_name || selectedAreaData.name}
                          className="aspect-video"
                        />
                        
                        {/* Content */}
                        <div className="p-4 space-y-4">
                          {/* IMPROVED Sensory Display: Label → Explanation → Score */}
                          <div className="bg-white/5 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight) }}
                              />
                              <span className="text-white font-semibold text-sm">
                                {selectedAreaData.label} sensory environment
                              </span>
                            </div>
                            <p className="text-white/70 text-sm">
                              {getAreaSummary(selectedAreaData.categories)?.summary || 'Generally manageable for most visitors.'}
                            </p>
                          </div>
                          
                          {/* Full Description (EXACT USER INPUT) */}
                          {selectedAreaData.description && (
                            <div>
                              <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2">About this area</h4>
                              <p className="text-white/80 text-sm leading-relaxed">
                                {selectedAreaData.description}
                              </p>
                            </div>
                          )}
                          
                          {/* Support Indicators */}
                          {selectedAreaData.support_indicators && Object.values(selectedAreaData.support_indicators).some(v => v) && (
                            <div>
                              <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2">Support Available</h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedAreaData.support_indicators.seating_available && (
                                  <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium border border-emerald-500/30">
                                    Seating Available
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.step_free_access && (
                                  <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium border border-emerald-500/30">
                                    Step-Free Access
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.accessible_toilet_nearby && (
                                  <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium border border-emerald-500/30">
                                    Accessible Toilet
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.staff_present && (
                                  <span className="px-2.5 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium border border-blue-500/30">
                                    Staff Present
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.assistance_available && (
                                  <span className="px-2.5 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium border border-blue-500/30">
                                    Assistance Available
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.quiet_space_nearby && (
                                  <span className="px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium border border-purple-500/30">
                                    Quiet Space Nearby
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.lower_lighting_available && (
                                  <span className="px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium border border-purple-500/30">
                                    Lower Lighting
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.calm_waiting_area && (
                                  <span className="px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium border border-purple-500/30">
                                    Calm Waiting Area
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.clear_signage && (
                                  <span className="px-2.5 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-medium border border-amber-500/30">
                                    Clear Signage
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.map_available && (
                                  <span className="px-2.5 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-medium border border-amber-500/30">
                                    Map Available
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.timed_entry_available && (
                                  <span className="px-2.5 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-xs font-medium border border-indigo-500/30">
                                    Timed Entry
                                  </span>
                                )}
                                {selectedAreaData.support_indicators.managed_queue_system && (
                                  <span className="px-2.5 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-xs font-medium border border-indigo-500/30">
                                    Managed Queue
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          <div className="space-y-2">
                            <button 
                              onClick={() => goToScreen(SCREENS.BREAKDOWN, selectedAreaData)}
                              className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              View Sensory Details
                            </button>
                            <button 
                              onClick={() => handlePreviewRoute(selectedAreaData)}
                              data-testid="preview-route-btn"
                              className="w-full text-white text-sm py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                              style={{ backgroundColor: '#22c55e' }}
                            >
                              <Navigation className="w-4 h-4" />
                              PREVIEW YOUR ROUTE
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* MOBILE DETAIL PANEL - Slide up sheet */}
                <AnimatePresence>
                  {mobileDetailOpen && selectedAreaData && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
                      onClick={() => setMobileDetailOpen(false)}
                    >
                      <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 25 }}
                        className="absolute bottom-0 left-0 right-0 rounded-t-3xl max-h-[85vh] overflow-y-auto"
                        style={{ backgroundColor: '#1e3a5f' }}
                        onClick={e => e.stopPropagation()}
                      >
                        {/* Handle bar */}
                        <div className="flex justify-center py-3">
                          <div className="w-10 h-1 bg-white/30 rounded-full" />
                        </div>
                        
                        {/* Header */}
                        <div className="px-4 pb-3 flex items-center justify-between border-b border-white/10">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-semibold">{selectedAreaData.area_name || selectedAreaData.name}</span>
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-bold"
                              style={{ 
                                backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight),
                                color: 'white'
                              }}
                            >
                              {selectedAreaData.label}
                            </span>
                          </div>
                          <button onClick={() => setMobileDetailOpen(false)} className="p-2">
                            <X className="w-5 h-5 text-white/70" />
                          </button>
                        </div>
                        
                        {/* Area Images - Multi-image gallery support */}
                        <ImageGallery 
                          photos={selectedAreaData.photos} 
                          areaName={selectedAreaData.area_name || selectedAreaData.name}
                          className="aspect-video"
                        />
                        
                        {/* Content */}
                        <div className="p-4 space-y-4">
                          {/* IMPROVED Sensory Display: Label → Explanation */}
                          <div className="bg-white/5 rounded-xl p-3">
                            <div className="flex items-center gap-2 mb-2">
                              <span 
                                className="w-3 h-3 rounded-full"
                                style={{ backgroundColor: getTrafficLightColor(selectedAreaData.trafficLight) }}
                              />
                              <span className="text-white font-semibold text-sm">
                                {selectedAreaData.label} sensory environment
                              </span>
                            </div>
                            <p className="text-white/70 text-sm">
                              {getAreaSummary(selectedAreaData.categories)?.summary || 'Generally manageable for most visitors.'}
                            </p>
                          </div>
                          
                          {/* Full Description (EXACT USER INPUT) */}
                          {selectedAreaData.description && (
                            <div>
                              <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2">About this area</h4>
                              <p className="text-white/80 text-sm leading-relaxed">
                                {selectedAreaData.description}
                              </p>
                            </div>
                          )}
                          
                          {/* Support Indicators - Mobile */}
                          {selectedAreaData.support_indicators && Object.values(selectedAreaData.support_indicators).some(v => v) && (
                            <div>
                              <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2">Support Available</h4>
                              <div className="flex flex-wrap gap-2">
                                {selectedAreaData.support_indicators.seating_available && (
                                  <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium border border-emerald-500/30">Seating Available</span>
                                )}
                                {selectedAreaData.support_indicators.step_free_access && (
                                  <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium border border-emerald-500/30">Step-Free Access</span>
                                )}
                                {selectedAreaData.support_indicators.accessible_toilet_nearby && (
                                  <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-300 rounded-full text-xs font-medium border border-emerald-500/30">Accessible Toilet</span>
                                )}
                                {selectedAreaData.support_indicators.staff_present && (
                                  <span className="px-2.5 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium border border-blue-500/30">Staff Present</span>
                                )}
                                {selectedAreaData.support_indicators.assistance_available && (
                                  <span className="px-2.5 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs font-medium border border-blue-500/30">Assistance Available</span>
                                )}
                                {selectedAreaData.support_indicators.quiet_space_nearby && (
                                  <span className="px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium border border-purple-500/30">Quiet Space Nearby</span>
                                )}
                                {selectedAreaData.support_indicators.lower_lighting_available && (
                                  <span className="px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium border border-purple-500/30">Lower Lighting</span>
                                )}
                                {selectedAreaData.support_indicators.calm_waiting_area && (
                                  <span className="px-2.5 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs font-medium border border-purple-500/30">Calm Waiting Area</span>
                                )}
                                {selectedAreaData.support_indicators.clear_signage && (
                                  <span className="px-2.5 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-medium border border-amber-500/30">Clear Signage</span>
                                )}
                                {selectedAreaData.support_indicators.map_available && (
                                  <span className="px-2.5 py-1 bg-amber-500/20 text-amber-300 rounded-full text-xs font-medium border border-amber-500/30">Map Available</span>
                                )}
                                {selectedAreaData.support_indicators.timed_entry_available && (
                                  <span className="px-2.5 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-xs font-medium border border-indigo-500/30">Timed Entry</span>
                                )}
                                {selectedAreaData.support_indicators.managed_queue_system && (
                                  <span className="px-2.5 py-1 bg-indigo-500/20 text-indigo-300 rounded-full text-xs font-medium border border-indigo-500/30">Managed Queue</span>
                                )}
                              </div>
                            </div>
                          )}
                          
                          <div className="space-y-2 pt-2">
                            <button 
                              onClick={() => { setMobileDetailOpen(false); goToScreen(SCREENS.BREAKDOWN, selectedAreaData); }}
                              className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2.5 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                            >
                              <Eye className="w-4 h-4" />
                              View Sensory Details
                            </button>
                            <button 
                              onClick={() => { setMobileDetailOpen(false); handlePreviewRoute(selectedAreaData); }}
                              data-testid="mobile-preview-route-btn"
                              className="w-full text-white text-sm py-3.5 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                              style={{ backgroundColor: '#22c55e' }}
                            >
                              <Navigation className="w-4 h-4" />
                              PREVIEW YOUR ROUTE
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* FOOTER BAR */}
                <div className="fixed bottom-0 left-0 right-0 bg-[#1a365d]/95 backdrop-blur-xl border-t border-white/10 px-4 py-3 safe-area-bottom z-40">
                  <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <div className="hidden sm:flex items-center gap-4 text-white/60 text-xs">
                      <a href="#" className="hover:text-white flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> Location
                      </a>
                      <a href="#" className="hover:text-white flex items-center gap-1">
                        <Info className="w-3 h-3" /> Help
                      </a>
                    </div>
                    <button 
                      onClick={() => selectedAreaData && handlePreviewRoute(selectedAreaData)}
                      disabled={!selectedAreaData}
                      className="flex-1 sm:flex-none text-white py-3 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                      style={{ backgroundColor: '#22c55e' }}
                      data-testid="footer-preview-visit-btn"
                    >
                      <Play className="w-4 h-4" />
                      PREVIEW YOUR VISIT
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* ==================== TIER 1: PREVIEW ROUTE ==================== */}
            {currentScreen === SCREENS.PREVIEW_ROUTE && routeTargetArea && (
              <motion.div
                key="preview-route"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="h-full pb-4"
              >
                {/* ROUTE HEADER */}
                <div className="bg-[#1a365d] -mx-6 -mt-4 px-4 py-3 mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {logoUrl ? (
                      <img src={logoUrl} alt="Logo" className="h-7 object-contain" />
                    ) : (
                      <span className="text-white font-bold text-lg tracking-wide">nysa</span>
                    )}
                    <span className="text-white/90 text-sm font-medium">Preview Your Visit</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Journey Edit Mode Toggle */}
                    <button 
                      onClick={() => setJourneyEditMode(!journeyEditMode)}
                      data-testid="journey-edit-toggle"
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-all ${
                        journeyEditMode 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                      }`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span>{journeyEditMode ? 'Editing' : 'Build Journey'}</span>
                    </button>
                    <button 
                      onClick={() => goToScreen(SCREENS.EXPLORE_HUB)}
                      className="p-2 text-white/70 hover:text-white flex items-center gap-1 text-sm"
                    >
                      <Home className="w-4 h-4" />
                      <span className="hidden sm:inline">Home</span>
                    </button>
                    <button 
                      onClick={() => setMenuOpen(true)}
                      className="p-2 text-white/70 hover:text-white"
                    >
                      <Menu className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {(() => {
                  const route = generateRouteToArea(routeTargetArea);
                  const currentStep = route[currentStepIndex] || route[0];
                  const nextStep = route[currentStepIndex + 1];
                  const exitOptions = getExitOptions();
                  
                  // Get only area steps (not transitions) for progress bar
                  const areaSteps = route.filter(s => !s.isTransition);
                  const currentAreaIndex = areaSteps.findIndex(s => s.area_id === currentStep?.area_id);
                  const movementText = currentStep ? getMovementText(currentStep, nextStep, currentStep.isTransition) : null;
                  
                  return (
                    <div className="flex flex-col gap-4 pb-20">
                      
                      {/* ========== PROGRESS BAR ========== */}
                      <div 
                        className="backdrop-blur-xl rounded-xl p-4 border border-white/10"
                        style={{ backgroundColor: '#1e3a5fe6' }}
                      >
                        <div className="flex items-center justify-between gap-2 overflow-x-auto">
                          {areaSteps.map((step, idx) => {
                            const isCompleted = route.indexOf(step) < currentStepIndex;
                            const isCurrent = step.area_id === currentStep?.area_id || 
                                            (currentStep?.isTransition && idx === currentAreaIndex);
                            return (
                              <React.Fragment key={step.area_id}>
                                <button
                                  onClick={() => setCurrentStepIndex(route.indexOf(step))}
                                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                                    isCurrent
                                      ? 'bg-white text-[#1a365d]'
                                      : isCompleted
                                        ? 'bg-emerald-500/30 text-emerald-300'
                                        : 'bg-white/10 text-white/60 hover:bg-white/20'
                                  }`}
                                >
                                  {isCompleted && <Check className="w-3 h-3" />}
                                  <span className="truncate max-w-[80px]">{step.area_name || step.name}</span>
                                </button>
                                {idx < areaSteps.length - 1 && (
                                  <ArrowRight className="w-4 h-4 text-white/30 flex-shrink-0" />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>

                      <div className="flex flex-col lg:flex-row gap-4">
                      
                      {/* LEFT COLUMN - Route Steps List */}
                      <div className="lg:w-72 flex-shrink-0">
                        <div 
                          className="backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10"
                          style={{ backgroundColor: '#1e3a5fe6' }}
                        >
                          <div className="px-4 py-3 border-b border-white/10">
                            <h2 className="text-white/90 text-xs font-semibold uppercase tracking-wider">
                              Your Route to {routeTargetArea.area_name || routeTargetArea.name}
                            </h2>
                            <p className="text-white/50 text-xs mt-1">
                              {journeyEditMode 
                                ? 'Tap + to add custom movement steps' 
                                : 'Typical journey through the venue'}
                            </p>
                          </div>
                          
                          {/* Edit Mode Banner */}
                          {journeyEditMode && (
                            <div className="px-4 py-2 bg-amber-500/20 border-b border-amber-500/30 flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Edit3 className="w-4 h-4 text-amber-400" />
                                <span className="text-amber-300 text-xs font-medium">Journey Edit Mode</span>
                              </div>
                              <button
                                onClick={() => setJourneyEditMode(false)}
                                className="text-amber-400 text-xs hover:text-amber-300 underline"
                              >
                                Done
                              </button>
                            </div>
                          )}
                          
                          {/* Route Steps with Transitions and Journey Edit Mode */}
                          <div className="max-h-[50vh] overflow-y-auto">
                            {route.map((step, index) => {
                              const isTransition = step.isTransition;
                              const isCustomStep = step.isCustom;
                              const TransIcon = isTransition ? (TRANSITION_TYPES[step.transitionType]?.icon || ArrowRight) : null;
                              
                              // Determine if we should show a "+" button after this step
                              // Show "+" after area steps (not after transitions) in edit mode
                              const showAddButton = journeyEditMode && !isTransition && index < route.length - 1;
                              const nextStep = route[index + 1];
                              // Find next area step (skip the transition to get the actual area)
                              const nextAreaStep = nextStep?.isTransition ? route[index + 2] : nextStep;
                              
                              return (
                                <React.Fragment key={step.area_id || index}>
                                  {/* The Step Button */}
                                  <div
                                    className={`w-full px-4 py-3 flex items-center gap-3 text-left transition-all border-b border-white/5 ${
                                      currentStepIndex === index 
                                        ? isTransition ? 'bg-amber-500/10' : 'bg-white/15' 
                                        : isTransition ? 'bg-white/5 hover:bg-white/10' : 'hover:bg-white/10'
                                    }`}
                                  >
                                    {/* Click area for step selection */}
                                    <button
                                      onClick={() => setCurrentStepIndex(index)}
                                      data-testid={`route-step-${index}`}
                                      className="flex items-center gap-3 flex-1 min-w-0"
                                    >
                                      {/* Step indicator */}
                                      {isTransition ? (
                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                          isCustomStep ? 'bg-emerald-500/30' : 'bg-amber-500/20'
                                        }`}>
                                          {isCustomStep ? (
                                            <Edit3 className="w-3 h-3 text-emerald-400" />
                                          ) : (
                                            <TransIcon className="w-3 h-3 text-amber-400" />
                                          )}
                                        </span>
                                      ) : (
                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                                          index < currentStepIndex 
                                            ? 'bg-emerald-500 text-white'
                                            : currentStepIndex === index
                                              ? 'bg-white text-[#1a365d]'
                                              : 'bg-white/20 text-white/70'
                                        }`}>
                                          {index < currentStepIndex ? <Check className="w-3 h-3" /> : areaSteps.indexOf(step) + 1}
                                        </span>
                                      )}
                                      
                                      <div className="flex-1 min-w-0">
                                        <span className={`text-sm truncate block ${
                                          isTransition 
                                            ? isCustomStep ? 'text-emerald-300 font-medium' : 'text-amber-300/80 italic'
                                            : 'text-white'
                                        }`}>
                                          {isTransition ? step.transitionLabel : (step.area_name || step.name)}
                                        </span>
                                        <span className="text-white/50 text-xs">
                                          {isTransition ? (
                                            <span className={isCustomStep ? 'text-emerald-400/70' : 'text-amber-400/70'}>
                                              {isCustomStep ? 'Custom step' : step.transitionDuration}
                                            </span>
                                          ) : currentStepIndex === index ? (
                                            <span className="text-emerald-400 font-medium">You are here</span>
                                          ) : index === currentStepIndex + 1 ? (
                                            <span className="text-amber-400">Next</span>
                                          ) : step.stepType === 'start' ? 'Start' : 
                                           step.stepType === 'destination' ? 'Destination' : 
                                           'Stop'}
                                        </span>
                                      </div>
                                    </button>
                                    
                                    {/* Edit/Delete buttons for custom steps in edit mode */}
                                    {journeyEditMode && isTransition && isCustomStep && (
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const customStep = movementSteps.find(s => s.step_id === step.customStepId);
                                            if (customStep) openEditStepEditor(customStep);
                                          }}
                                          data-testid={`edit-step-${index}`}
                                          className="p-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors"
                                        >
                                          <Edit3 className="w-3 h-3" />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteMovementStep(step.customStepId);
                                          }}
                                          data-testid={`delete-step-${index}`}
                                          className="p-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-colors"
                                        >
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* ADD MOVEMENT STEP BUTTON - shows between area steps in edit mode */}
                                  {showAddButton && (
                                    <button
                                      onClick={() => {
                                        // Find the transition between this area and next
                                        // The from_area is the current step, to_area is the next area step
                                        openAddStepEditor(
                                          step.area_id,
                                          nextAreaStep?.area_id,
                                          step.area_name || step.name,
                                          nextAreaStep?.area_name || nextAreaStep?.name
                                        );
                                      }}
                                      data-testid={`add-step-after-${index}`}
                                      className="w-full py-2 flex items-center justify-center gap-2 bg-amber-500/10 hover:bg-amber-500/20 border-y border-amber-500/20 text-amber-400 text-xs font-medium transition-all group"
                                    >
                                      <Plus className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                                      <span>Add movement step</span>
                                    </button>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                          
                          {/* Exit Options */}
                          {exitOptions.length > 0 && (
                            <>
                              <div className="px-4 py-2 border-t border-white/10 bg-white/5">
                                <h3 className="text-white/60 text-xs uppercase tracking-wider">Quiet Exits</h3>
                              </div>
                              <div>
                                {exitOptions.map((exit, i) => (
                                  <div key={exit.area_id || i} className="px-4 py-2 flex items-center gap-2 text-white/70 text-sm border-b border-white/5 last:border-0">
                                    <DoorOpen className="w-4 h-4 text-emerald-400" />
                                    <span className="truncate">{exit.area_name || exit.name}</span>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                        
                        {/* Confidence Badge */}
                        <div className="mt-3 bg-emerald-500/20 border border-emerald-500/30 rounded-xl px-4 py-3">
                          <div className="flex items-center gap-2 mb-1">
                            <Shield className="w-5 h-5 text-emerald-400" />
                            <span className="text-emerald-300 text-sm font-semibold">You can do this</span>
                          </div>
                          <p className="text-emerald-300/70 text-xs pl-7">
                            Each step is manageable. Take your time.
                          </p>
                        </div>
                      </div>
                      
                      {/* MIDDLE COLUMN - Visual Flow (Desktop) - Simplified */}
                      <div className="hidden lg:flex flex-1 items-center justify-center overflow-x-auto py-4">
                        <div className="flex items-center gap-2">
                          {route.filter(s => !s.isTransition).map((step, index) => {
                            const fullIndex = route.indexOf(step);
                            const isCompleted = fullIndex < currentStepIndex;
                            const isCurrent = fullIndex === currentStepIndex || 
                                            (route[currentStepIndex]?.isTransition && index === Math.floor(currentStepIndex / 2));
                            
                            return (
                              <React.Fragment key={step.area_id}>
                                {/* Step Card */}
                                <motion.div
                                  initial={{ opacity: 0, scale: 0.9 }}
                                  animate={{ opacity: 1, scale: 1 }}
                                  transition={{ delay: index * 0.1 }}
                                  onClick={() => setCurrentStepIndex(fullIndex)}
                                  className={`relative bg-white/10 backdrop-blur-xl rounded-xl overflow-hidden cursor-pointer transition-all w-32 flex-shrink-0 ${
                                    isCurrent ? 'ring-2 ring-white/50 scale-105' : isCompleted ? 'opacity-70' : ''
                                  }`}
                                >
                                  <AreaImage 
                                    src={step.image} 
                                    alt={step.area_name || step.name}
                                    className="aspect-square"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                  <div className="absolute bottom-0 left-0 right-0 p-2">
                                    <span className="text-white text-xs font-medium block truncate">{step.area_name || step.name}</span>
                                  </div>
                                  {/* Status indicator */}
                                  <div className={`absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                    isCompleted 
                                      ? 'bg-emerald-500 text-white'
                                      : isCurrent
                                        ? 'bg-white text-[#1a365d]'
                                        : 'bg-white/30 text-white'
                                  }`}>
                                    {isCompleted ? <Check className="w-3 h-3" /> : index + 1}
                                  </div>
                                </motion.div>
                                
                                {/* Arrow/Transition indicator to next */}
                                {index < route.filter(s => !s.isTransition).length - 1 && (
                                  <div className="flex flex-col items-center px-1 flex-shrink-0">
                                    <div className="text-white/40 text-[10px] mb-1">→</div>
                                    <ArrowRight className="w-5 h-5 text-white/30" />
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* RIGHT COLUMN - Current Step Detail */}
                      <div className="lg:w-80 flex-shrink-0">
                        <div 
                          className={`backdrop-blur-xl rounded-2xl overflow-hidden border ${
                            currentStep?.isTransition 
                              ? currentStep?.isCustom
                                ? 'bg-emerald-900/30 border-emerald-500/30'
                                : 'bg-amber-900/30 border-amber-500/30' 
                              : 'border-white/10'
                          }`}
                          style={!currentStep?.isTransition ? { backgroundColor: '#1e3a5fe6' } : {}}
                        >
                          {/* Step Header with Movement Language */}
                          <div className={`px-4 py-3 ${
                            currentStep?.isTransition 
                              ? currentStep?.isCustom
                                ? 'bg-emerald-900/50'
                                : 'bg-amber-900/50'
                              : 'bg-[#1a365d]'
                          }`}>
                            {currentStep?.isTransition ? (
                              <>
                                <div className="flex items-center justify-between mb-1">
                                  <div className="flex items-center gap-2">
                                    {currentStep?.isCustom ? (
                                      <Edit3 className="w-4 h-4 text-emerald-400" />
                                    ) : (
                                      <ArrowRight className="w-4 h-4 text-amber-400" />
                                    )}
                                    <span className={`text-xs uppercase tracking-wider ${
                                      currentStep?.isCustom ? 'text-emerald-300' : 'text-amber-300'
                                    }`}>
                                      {currentStep?.isCustom ? 'Custom Movement Step' : 'Moving between spaces'}
                                    </span>
                                  </div>
                                  {/* Edit button for custom steps in edit mode */}
                                  {journeyEditMode && currentStep?.isCustom && (
                                    <button
                                      onClick={() => {
                                        const customStep = movementSteps.find(s => s.step_id === currentStep.customStepId);
                                        if (customStep) openEditStepEditor(customStep);
                                      }}
                                      className="p-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 transition-colors"
                                      data-testid="edit-current-step-btn"
                                    >
                                      <Edit3 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                                <h2 className="text-white font-semibold">{currentStep.transitionLabel}</h2>
                              </>
                            ) : (
                              <>
                                <h2 className="text-white font-semibold text-lg">{currentStep?.area_name || currentStep?.name}</h2>
                                {movementText?.progression && (
                                  <p className="text-emerald-400 text-sm mt-1">{movementText.progression}</p>
                                )}
                              </>
                            )}
                          </div>
                          
                          {/* Step Image (only for areas, not transitions) */}
                          {!currentStep?.isTransition && (
                            <AreaImage 
                              src={currentStep?.image} 
                              alt={currentStep?.area_name || currentStep?.name}
                              className="aspect-video"
                            />
                          )}
                          
                          {/* Step Content */}
                          <div className="p-4 space-y-3">
                            {/* CUSTOM STEP: Show custom description and awareness */}
                            {currentStep?.isTransition && currentStep?.isCustom ? (
                              <>
                                {/* Custom Description */}
                                {currentStep.transitionDescription && (
                                  <p className="text-white font-medium">
                                    {currentStep.transitionDescription}
                                  </p>
                                )}
                                
                                {/* Custom Awareness */}
                                {currentStep.transitionAwareness && (
                                  <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
                                    <div className="flex items-center gap-2 mb-1">
                                      <Bell className="w-4 h-4 text-emerald-400" />
                                      <span className="text-emerald-400 text-xs uppercase tracking-wider font-medium">Sensory Awareness</span>
                                    </div>
                                    <p className="text-emerald-200/80 text-sm">
                                      {currentStep.transitionAwareness}
                                    </p>
                                  </div>
                                )}
                                
                                {/* Duration */}
                                <div className="flex items-center gap-2 text-emerald-300 text-sm">
                                  <Clock className="w-4 h-4" />
                                  <span>About {currentStep.transitionDuration}</span>
                                </div>
                                
                                {/* From/To indicator */}
                                <div className="flex items-center gap-2 text-white/50 text-xs pt-1">
                                  <span>{currentStep.fromArea}</span>
                                  <ArrowRight className="w-3 h-3" />
                                  <span>{currentStep.toArea}</span>
                                </div>
                              </>
                            ) : (
                              <>
                                {/* PRIMARY LINE - What happens (default transition or area) */}
                                {movementText?.primary && (
                                  <p className="text-white font-medium">
                                    {movementText.primary}
                                  </p>
                                )}
                                
                                {/* SECONDARY LINE - How it feels */}
                                {movementText?.secondary && (
                                  <p className={`text-sm ${currentStep?.isTransition ? 'text-amber-200/80' : 'text-white/70'}`}>
                                    {movementText.secondary}
                                  </p>
                                )}
                                
                                {/* Transition duration (default transitions) */}
                                {currentStep?.isTransition && !currentStep?.isCustom && (
                                  <div className="flex items-center gap-2 text-amber-300 text-sm">
                                    <Clock className="w-4 h-4" />
                                    <span>About {currentStep.transitionDuration}</span>
                                  </div>
                                )}
                                
                                {/* REASSURANCE */}
                                {!currentStep?.isTransition && (
                                  <p className="text-emerald-400/80 text-xs italic">
                                    You can pause here if needed.
                                  </p>
                                )}
                                
                                {/* MAX 2-3 TAGS - Sensory Summary (areas only) */}
                                {!currentStep?.isTransition && (currentStep?.categories || []).length > 0 && (
                                  <div className="flex flex-wrap gap-2 pt-1">
                                    {(currentStep?.categories || []).slice(0, 2).map((cat, i) => (
                                      <span 
                                        key={i}
                                        className="px-2 py-1 rounded-lg text-xs font-medium"
                                        style={{
                                          backgroundColor: `${getTrafficLightColor(cat.traffic_light)}20`,
                                          color: getTrafficLightColor(cat.traffic_light)
                                        }}
                                      >
                                        {(cat.category || '').split(' ')[0]}: {getTrafficLightLabel(cat.traffic_light)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </>
                            )}
                            
                            {/* Action Buttons */}
                            <div className="space-y-2 pt-2">
                              {exitOptions.length > 0 && !currentStep?.isTransition && (
                                <button className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-xl transition-colors flex items-center gap-2">
                                  <Coffee className="w-4 h-4" />
                                  <span>Need a break?</span>
                                  <ChevronRight className="w-4 h-4 ml-auto" />
                                </button>
                              )}
                              <button 
                                onClick={() => goToScreen(SCREENS.EXPLORE_HUB)}
                                className="w-full bg-white/10 hover:bg-white/20 text-white text-sm py-2 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                              >
                                <ArrowLeft className="w-4 h-4" />
                                Exit Route
                              </button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Navigation Controls */}
                        <div className="mt-3 flex gap-2">
                          <button
                            onClick={() => setCurrentStepIndex(Math.max(0, currentStepIndex - 1))}
                            disabled={currentStepIndex === 0}
                            className="flex-1 bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                          >
                            <ChevronLeft className="w-4 h-4" />
                            Back
                          </button>
                          <button
                            onClick={() => setCurrentStepIndex(Math.min(route.length - 1, currentStepIndex + 1))}
                            disabled={currentStepIndex === route.length - 1}
                            className="flex-1 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-30 text-white py-3 px-4 rounded-xl transition-colors flex items-center justify-center gap-2"
                          >
                            Continue
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                    </div>
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        {/* ==================== SCREEN INDICATOR DOTS (Legacy screens only) ==================== */}
        {currentScreen !== SCREENS.BREAKDOWN && 
         currentScreen !== SCREENS.EXPLORE_HUB && 
         currentScreen !== SCREENS.PREVIEW_ROUTE && (
          <div className="flex items-center justify-center gap-2 pb-8 safe-area-bottom">
            {screenOrder.map((screen, index) => (
              <button
                key={screen}
                onClick={() => goToScreen(screen)}
                className={`h-2 rounded-full transition-all duration-300 ${
                  index === currentScreenIndex 
                    ? 'bg-white w-6' 
                    : 'bg-white/30 hover:bg-white/50 w-2'
                }`}
                aria-label={`Go to ${screen}`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* ==================== JOURNEY BUILDER: STEP EDITOR MODAL ==================== */}
      <AnimatePresence>
        {showStepEditor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setShowStepEditor(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-white/20"
              style={{ backgroundColor: '#1e3a5f' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-[#1a365d] px-5 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold">
                    {editingStep ? 'Edit Movement Step' : 'Add Movement Step'}
                  </h3>
                  <p className="text-white/60 text-xs mt-0.5">
                    {stepEditorData.from_name} → {stepEditorData.to_name}
                  </p>
                </div>
                <button 
                  onClick={() => setShowStepEditor(false)}
                  className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Body */}
              <div className="p-5 space-y-4">
                {/* Title */}
                <div>
                  <Label className="text-white/80 text-sm mb-1.5 block">
                    Step Title <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    value={stepEditorData.title}
                    onChange={(e) => setStepEditorData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Walking to the Café"
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-500"
                    data-testid="step-title-input"
                  />
                </div>
                
                {/* Description */}
                <div>
                  <Label className="text-white/80 text-sm mb-1.5 block">Description</Label>
                  <Textarea
                    value={stepEditorData.description}
                    onChange={(e) => setStepEditorData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What will the visitor experience during this movement?"
                    rows={3}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-500 resize-none"
                    data-testid="step-description-input"
                  />
                </div>
                
                {/* Awareness */}
                <div>
                  <Label className="text-white/80 text-sm mb-1.5 block">
                    Sensory Awareness
                    <span className="text-white/40 font-normal ml-1">(optional)</span>
                  </Label>
                  <Textarea
                    value={stepEditorData.awareness}
                    onChange={(e) => setStepEditorData(prev => ({ ...prev, awareness: e.target.value }))}
                    placeholder="Any sensory considerations? e.g., May be louder near the kitchen"
                    rows={2}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-500 resize-none"
                    data-testid="step-awareness-input"
                  />
                </div>
                
                {/* Duration */}
                <div>
                  <Label className="text-white/80 text-sm mb-1.5 block">Duration</Label>
                  <div className="flex gap-2">
                    {['10-15 seconds', '20-30 seconds', '1-2 minutes'].map(dur => (
                      <button
                        key={dur}
                        onClick={() => setStepEditorData(prev => ({ ...prev, duration: dur }))}
                        className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                          stepEditorData.duration === dur
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white/10 text-white/70 hover:bg-white/20'
                        }`}
                        data-testid={`duration-${dur.replace(/\s/g, '-')}`}
                      >
                        {dur}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="px-5 pb-5 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowStepEditor(false)}
                  className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                  data-testid="cancel-step-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveMovementStep}
                  disabled={savingStep || !stepEditorData.title.trim()}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
                  data-testid="save-step-btn"
                >
                  {savingStep ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {editingStep ? 'Update Step' : 'Add Step'}
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* ==================== WEEKLY BUSYNESS EDITOR MODAL ==================== */}
      <AnimatePresence>
        {showBusynessEditor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setShowBusynessEditor(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25 }}
              className="w-full max-w-2xl rounded-2xl overflow-hidden shadow-2xl border border-white/20 my-8"
              style={{ backgroundColor: '#1e3a5f' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="bg-[#1a365d] px-5 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold">Typical Visitor Levels</h3>
                  <p className="text-white/60 text-xs mt-0.5">Show how busy your venue usually feels across the week.</p>
                </div>
                <button 
                  onClick={() => setShowBusynessEditor(false)}
                  className="p-2 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Modal Body - Weekly Grid */}
              <div className="p-5">
                {/* Grid Header */}
                <div className="grid grid-cols-4 gap-2 mb-3">
                  <div></div>
                  <div className="text-center text-white/60 text-xs font-medium">Morning<br/><span className="text-white/40">6am-12pm</span></div>
                  <div className="text-center text-white/60 text-xs font-medium">Afternoon<br/><span className="text-white/40">12pm-5pm</span></div>
                  <div className="text-center text-white/60 text-xs font-medium">Evening<br/><span className="text-white/40">5pm-12am</span></div>
                </div>
                
                {/* Days */}
                {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => (
                  <div key={day} className="grid grid-cols-4 gap-2 mb-2">
                    <div className="text-white/80 text-sm font-medium capitalize flex items-center">
                      {day.slice(0, 3)}
                    </div>
                    {['morning', 'afternoon', 'evening'].map(timeBlock => {
                      const currentValue = weeklyBusyness?.[day]?.[timeBlock];
                      const levels = [
                        { value: null, label: '—', color: 'bg-white/10 text-white/40' },
                        { value: 'quiet', label: 'Quiet', color: 'bg-emerald-500/30 text-emerald-400 border-emerald-500/50' },
                        { value: 'moderate', label: 'Mod', color: 'bg-amber-500/30 text-amber-400 border-amber-500/50' },
                        { value: 'busy', label: 'Busy', color: 'bg-orange-500/30 text-orange-400 border-orange-500/50' },
                        { value: 'very_busy', label: 'V.Busy', color: 'bg-red-500/30 text-red-400 border-red-500/50' }
                      ];
                      const currentLevel = levels.find(l => l.value === currentValue) || levels[0];
                      
                      return (
                        <div key={timeBlock} className="relative">
                          <button
                            onClick={() => {
                              // Cycle through levels
                              const currentIndex = levels.findIndex(l => l.value === currentValue);
                              const nextIndex = (currentIndex + 1) % levels.length;
                              updateBusynessBlock(day, timeBlock, levels[nextIndex].value);
                            }}
                            data-testid={`busyness-${day}-${timeBlock}`}
                            className={`w-full py-2 px-2 rounded-lg text-xs font-medium transition-all border ${currentLevel.color} hover:opacity-80`}
                          >
                            {currentLevel.label}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ))}
                
                {/* Legend */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10">
                  <span className="text-white/40 text-xs">Tap to cycle:</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/30 text-emerald-400">Quiet</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-amber-500/30 text-amber-400">Moderate</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-orange-500/30 text-orange-400">Busy</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-red-500/30 text-red-400">Very Busy</span>
                </div>
                
                {/* Notes */}
                <div className="mt-4">
                  <Label className="text-white/80 text-sm mb-1.5 block">Additional notes (optional)</Label>
                  <Textarea
                    value={busynessNotes}
                    onChange={(e) => setBusynessNotes(e.target.value)}
                    placeholder="e.g., Hockey games make Saturday afternoons busier"
                    rows={2}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-500 resize-none"
                    data-testid="busyness-notes-input"
                  />
                </div>
              </div>
              
              {/* Modal Footer */}
              <div className="px-5 pb-5 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBusynessEditor(false)}
                  className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                  data-testid="cancel-busyness-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveWeeklyBusyness}
                  disabled={savingBusyness}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
                  data-testid="save-busyness-btn"
                >
                  {savingBusyness ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Visitor Levels
                    </>
                  )}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GuidePage;
